import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import { tokenStore } from "@/lib/token";
import axios from "axios";
import { toast } from "sonner";
import AmbassadorsPanel from "@/components/admin/AmbassadorsPanel";
import { Shield, Users, Bot, Settings, Radio, DollarSign, Plus, Trash2, Edit, Key, RefreshCw, Search, Gift, LayoutDashboard, CreditCard, Tag, ArrowRightLeft, Store, ChevronDown, ChevronUp, FileText, Headphones, MessageCircle, CheckCircle, Clock, AlertCircle, Send, Star, Upload, Brain, Activity, Megaphone, ArrowLeft, AlertTriangle, LayoutTemplate, Eye, UserCog, Menu, X, Copy, Power, Download, BarChart3, KeyRound, ShieldAlert, Link2, PartyPopper, Ban, Sparkles, HardDrive } from "lucide-react";
import Navbar from "@/components/Navbar";
import PageBuilder from "@/components/admin/PageBuilder";
import SeasonPanel from "@/components/admin/SeasonPanel";
import NetbanPanel from "@/components/admin/NetbanPanel";
import AllBotsPanel from "@/components/admin/AllBotsPanel";
import FounderScripts from "@/components/admin/FounderScripts";
import VantaAIPanel from "@/components/admin/VantaAIPanel";
import VantaAIClients from "@/components/admin/VantaAIClients";
import VantaAIChats from "@/components/admin/VantaAIChats";
import VantaAITraining from "@/components/admin/VantaAITraining";
import VantaAIModeration from "@/components/admin/VantaAIModeration";
import { ProductExtrasFields, ReleaseDialog, ProductStatsPanel, LicensesPanel, ProtectionPanel, TrialsPanel } from "@/components/admin/ShopAdminPanels";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Affichage seulement : c'est le serveur qui décide (email exact du fondateur ou staff).
const ADMIN_EMAILS = ["tilyesgamer@gmail.com"];

// Permissions par rôle. Le staff n'a que 2 rôles possibles : "support" (accès
// restreint : consulter les utilisateurs/abonnements + gérer le support, rien d'autre)
// et "admin" (accès complet, identique au fondateur).
const ROLE_PERMISSIONS = {
  admin: ['overview', 'product-stats', 'boutique', 'promo-codes', 'trials', 'reviews', 'users', 'subscriptions', 'support', 'ai-config', 'licenses', 'protection', 'info', 'site-status', 'season', 'api-keys', 'netbans', 'ambassadors', 'vanta-ai', 'vai-clients', 'vai-chats', 'vai-training', 'vai-moderation', 'all-bots', 'scripts'],
  support: ['users', 'subscriptions', 'support'],
  // Développeur : outils techniques uniquement (pas de clients, paiements, clés ni staff).
  developer: ['vai-training', 'vai-moderation', 'all-bots', 'netbans', 'product-stats', 'info'],
};

// Menu de gauche : une rubrique par thème. Ce qui va ensemble est rangé dans la
// même rubrique, en onglets (ex. Clients → Utilisateurs / Abonnements).
const NAV_SECTIONS = [
  {
    id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard, hint: "Vue d'ensemble, statistiques", tabs: [
      { id: "overview", label: "Vue d'ensemble", icon: LayoutDashboard },
      { id: "product-stats", label: "Statistiques", icon: BarChart3 },
    ]
  },
  {
    id: "shop", label: "Boutique", icon: Store, hint: "Produits, promos, essais, avis", tabs: [
      { id: "boutique", label: "Produits", icon: Store },
      { id: "promo-codes", label: "Codes promo", icon: Tag },
      { id: "trials", label: "Essais gratuits", icon: Gift },
      { id: "reviews", label: "Avis", icon: Star },
    ]
  },
  {
    id: "clients", label: "Clients", icon: Users, hint: "Utilisateurs, abonnements, Vanta AI", tabs: [
      { id: "users", label: "Utilisateurs", icon: Users },
      { id: "subscriptions", label: "Abonnements", icon: CreditCard },
      { id: "vai-clients", label: "Vanta AI", icon: Sparkles },
    ]
  },
  {
    id: "bots", label: "Bots Discord", icon: Bot, hint: "Tous les bots : tableau de bord, console", tabs: [
      { id: "all-bots", label: "Tous les bots", icon: Bot },
    ]
  },
  {
    id: "helpdesk", label: "Support", icon: Headphones, hint: "Tickets, chatbot IA", tabs: [
      { id: "support", label: "Tickets", icon: MessageCircle, badge: "tickets" },
      { id: "ai-config", label: "Chatbot IA", icon: Brain },
    ]
  },
  {
    id: "licensing", label: "Licences", icon: KeyRound, hint: "Clés, protection anti-fuite", tabs: [
      { id: "licenses", label: "Clés de licence", icon: KeyRound },
      { id: "protection", label: "Protection", icon: ShieldAlert, badge: "alerts" },
    ]
  },
  {
    id: "moderation", label: "Modération", icon: Ban, hint: "Bans réseau des serveurs", tabs: [
      { id: "netbans", label: "Bans réseau", icon: Ban },
    ]
  },
  {
    id: "team", label: "Équipe", icon: Megaphone, hint: "Recrutement, ambassadeurs", tabs: [
      { id: "ambassadors", label: "Ambassadeurs", icon: Megaphone },
    ]
  },
  {
    id: "vanta-ai", label: "Vanta AI", icon: Sparkles, hint: "Moteur, conversations, entraînement", tabs: [
      { id: "vanta-ai", label: "Vue d'ensemble", icon: LayoutDashboard },
      { id: "vai-chats", label: "Conversations", icon: MessageCircle },
      { id: "vai-training", label: "Entraînement", icon: Brain },
      { id: "vai-moderation", label: "Modération", icon: ShieldAlert },
    ]
  },
  {
    id: "founder", label: "Mes scripts", icon: HardDrive, hint: "Code complet de tes produits (fondateur)", tabs: [
      { id: "scripts", label: "Mes scripts", icon: HardDrive },
    ]
  },
  {
    id: "site", label: "Site", icon: Settings, hint: "Annonces, statut, fêtes, clé API", tabs: [
      { id: "info", label: "Annonces", icon: Megaphone },
      { id: "site-status", label: "Statut & maintenance", icon: Activity },
      { id: "season", label: "Événements & fêtes", icon: PartyPopper },
      { id: "api-keys", label: "Clé API des bots", icon: Key },
    ]
  },
];

// Emplacements d'images de démonstration : celles déjà en place + une libre (3 au moins, 8 au plus).
const demoSlots = (images) => Array.from({ length: Math.min(8, Math.max(3, (images || []).filter((image) => image?.url || image).length + 1)) }, (_, index) => index);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Déterminer le rôle de l'utilisateur
  const userRole = user.role || (user.is_admin ? 'admin' : null);
  // Fondateur ou staff avec le rôle "admin" : accès complet. Un staff "support" ne l'est pas.
  // Attention : `user.is_admin` vaut true pour N'IMPORTE QUEL staff (support inclus, voir
  // /auth/me côté backend) — ce n'est PAS un signal fiable d'accès complet, d'où son absence ici.
  const isFullAdmin = ADMIN_EMAILS.includes(user.email) || userRole === 'admin';
  // Un staff "support" n'a pas accès à "Vue d'ensemble" : on l'atterrit directement sur
  // la première section qu'il peut voir plutôt que sur l'onglet par défaut du fondateur.
  const [tab, setTab] = useState(() => (!isFullAdmin && ROLE_PERMISSIONS[userRole]) ? ROLE_PERMISSIONS[userRole][0] : "overview");
  const [loading, setLoading] = useState(true);

  // Obtenir les permissions de l'utilisateur
  const getUserPermissions = () => {
    if (isFullAdmin) return ROLE_PERMISSIONS.admin;
    if (userRole && ROLE_PERMISSIONS[userRole]) return ROLE_PERMISSIONS[userRole];
    return [];
  };

  const userPermissions = getUserPermissions();
  const canManageAnnouncements = isFullAdmin;

  // /admin?tab=support : ouvre directement un onglet (liens de l'assistant IA, notifications).
  const location = useLocation();
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get("tab");
    if (wanted && userPermissions.includes(wanted)) setTab(wanted);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Vérifier si l'utilisateur a accès à une section
  const isFounder = ADMIN_EMAILS.includes(user.email);
  // « Mes scripts » : fondateur uniquement, même un staff admin ne le voit pas.
  const hasAccess = (sectionId) => userPermissions.includes(sectionId) && (sectionId !== 'scripts' || isFounder);

  // Rubriques et onglets visibles selon les permissions
  const navSections = NAV_SECTIONS.map(section => ({
    ...section,
    tabs: section.tabs.filter(t => hasAccess(t.id))
  })).filter(section => section.tabs.length > 0);
  const currentSection = navSections.find(section => section.tabs.some(t => t.id === tab)) || navSections[0];

  const [allUsers, setAllUsers] = useState([]);
  const [allBots, setAllBots] = useState([]);
  const [apiKeys, setApiKeys] = useState({});
  const [promoCodes, setPromoCodes] = useState([]);
  const [shopBots, setShopBots] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [reviews, setReviews] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [siteSettings, setSiteSettings] = useState({ maintenance_mode: false, maintenance_message: "" });
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [adminSidebarOpen, setAdminSidebarOpen] = useState(false);
  const [staffDialogUser, setStaffDialogUser] = useState(null);
  const [staffDialogRole, setStaffDialogRole] = useState("none");
  const [revenueMonth, setRevenueMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [revenueStats, setRevenueStats] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSort, setUserSort] = useState("date-desc");
  const [showEditBot, setShowEditBot] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [showAddPromo, setShowAddPromo] = useState(false);
  const [showTransfer, setShowTransfer] = useState(null);
  // Relier un ID Discord à un produit acheté avant que le client relie son Discord.
  const [showLinkDiscord, setShowLinkDiscord] = useState(null);
  const [linkDiscordId, setLinkDiscordId] = useState("");
  const [showLicense, setShowLicense] = useState(null);
  const [showAddFreeBot, setShowAddFreeBot] = useState(false);
  const [showAddShopBot, setShowAddShopBot] = useState(false);
  const [showEditShopBot, setShowEditShopBot] = useState(null);
  const [expandedBot, setExpandedBot] = useState(null);
  const [builderBot, setBuilderBot] = useState(null);
  const [releaseBot, setReleaseBot] = useState(null);
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [annForm, setAnnForm] = useState({ title: "", content: "", image_url: "" });
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [ticketFilter, setTicketFilter] = useState("all");
  const [ticketOpenOnly, setTicketOpenOnly] = useState(true);

  const [transferForm, setTransferForm] = useState({ new_discord_id: "", new_discord_token: "" });
  const [freeBotForm, setFreeBotForm] = useState({ userId: "", userLabel: "", botType: "", duration: "1" });
  // Vanta AI > Vue d'ensemble > « Les plus actifs » : ouvre ses conversations.
  const [vaiChatUser, setVaiChatUser] = useState(null);
  const [promoForm, setPromoForm] = useState({ code: "", discount_type: "percentage", discount_value: 10, applies_to: "all", visibility: "private", max_uses: 0, max_uses_per_user: 1, expires_at: "" });
  const [newShopBot, setNewShopBot] = useState({ id: "", name: "", color: "#5865F2", image_url: "", banner_url: "", description: "", features_text: "", demo_images: ["", "", ""], plans: [], downloadable: false, download_url: "", download_mode: "link", download_filename: "" });
  const [newPlan, setNewPlan] = useState({ name: "", price: 2, duration_days: 30, duration_label: "1 mois", duration_unit: "months", is_lifetime: false });

  const [openAlerts, setOpenAlerts] = useState(0);
  // Dernier onglet ouvert dans chaque rubrique : on y revient en recliquant la rubrique.
  const lastTabBySection = useRef({});
  const [aiKnowledge, setAiKnowledge] = useState("");
  const [aiSaving, setAiSaving] = useState(false);

  const tkn = () => tokenStore.get();
  const hdr = () => ({ Authorization: `Bearer ${tkn()}` });
  // Même chose mais stable (les panneaux Vanta AI rechargent quand elle change).
  const stableHdr = useCallback(() => ({ Authorization: `Bearer ${tokenStore.get()}` }), []);
  // Téléchargement avec le jeton dans l'en-tête, jamais dans l'adresse (sinon il
  // reste dans l'historique du navigateur et les journaux du serveur).
  const downloadCsv = async (path, filename) => {
    try {
      const res = await axios.get(`${API}${path}`, { headers: hdr(), responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };
  const exportCsv = (kind) => downloadCsv(`/admin/export/${kind}.csv`, `${kind}.csv`);

  const fetchAll = useCallback(async () => {
    try {
      const h = { Authorization: `Bearer ${tokenStore.get()}` };
      // Chaque appel échoue silencieusement vers un fallback vide pour ne jamais casser
      // l'affichage — mais on garde le statut HTTP pour repérer un blocage global (ex: 2FA
      // manquante côté admin) plutôt que de laisser croire que le panel est juste "vide".
      const guarded = (promise, fallback) => promise.catch((e) => ({ data: fallback, __status: e.response?.status, __detail: e.response?.data?.detail }));
      const [u, b, k, pr, sb, ann, tk, rv, st, aiK, ss] = await Promise.all([
        guarded(axios.get(`${API}/admin/users`, { headers: h }), []),
        guarded(axios.get(`${API}/admin/bots`, { headers: h }), []),
        guarded(axios.get(`${API}/admin/api-keys`, { headers: h }), {}),
        guarded(axios.get(`${API}/admin/promo-codes`, { headers: h }), []),
        guarded(axios.get(`${API}/admin/shop/bots`, { headers: h }), []),
        guarded(axios.get(`${API}/admin/announcements`, { headers: h }), []),
        guarded(axios.get(`${API}/admin/support/tickets`, { headers: h }), []),
        guarded(axios.get(`${API}/admin/reviews`, { headers: h }), []),
        guarded(axios.get(`${API}/admin/staff`, { headers: h }), []),
        guarded(axios.get(`${API}/support-ai/admin/knowledge`, { headers: h }), { content: "" }),
        guarded(axios.get(`${API}/admin/site-settings`, { headers: h }), { maintenance_mode: false, maintenance_message: "" }),
      ]);
      const results = [u, b, k, pr, sb, ann, tk, rv, st, aiK, ss];
      const blocked = results.find((r) => r.__status === 403 && r.__detail?.toLowerCase().includes("authentification"));
      setTwoFARequired(Boolean(blocked));

      setAllUsers(u.data || []);
      setAllBots(b.data || []);
      setApiKeys(k.data || {});
      setPromoCodes(pr.data || []);
      setShopBots(sb.data || []);
      setAnnouncements(ann.data || []);
      setTickets(tk.data || []);
      setReviews(rv.data || []);
      setStaffList(st.data || []);
      setAiKnowledge(aiK.data?.content || "");
      setSiteSettings(ss.data || { maintenance_mode: false, maintenance_message: "" });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    const hasAccess = user.is_admin || ADMIN_EMAILS.includes(user.email) || user.role === 'admin' || user.role === 'support';
    if (!hasAccess) { toast.error("Accès refusé"); navigate('/'); return; }
    fetchAll();
  }, [user, navigate, fetchAll]);

  // Pastille « Protection » du menu : alertes de triche pas encore traitées
  // (rafraîchie à chaque changement d'onglet).
  useEffect(() => {
    if (!isFullAdmin) return;
    axios.get(`${API}/admin/license-alerts`, { headers: hdr() })
      .then((r) => setOpenAlerts((r.data || []).length))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullAdmin, tab]);

  useEffect(() => {
    const section = NAV_SECTIONS.find((sec) => sec.tabs.some((t) => t.id === tab));
    if (section) lastTabBySection.current[section.id] = tab;
  }, [tab]);

  useEffect(() => {
    if (!user || tab !== "overview") return;
    setRevenueLoading(true);
    axios.get(`${API}/admin/stats/revenue?month=${revenueMonth}`, { headers: hdr() })
      .then((r) => setRevenueStats(r.data))
      .catch(() => setRevenueStats(null))
      .finally(() => setRevenueLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab, revenueMonth]);

  const exportRevenueRecap = () => {
    downloadCsv(`/admin/export/revenue-recap.csv?month=${revenueMonth}`, `recap-${revenueMonth}.csv`);
  };

  const revenueMonthOptions = Array.from({ length: 6 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    const value = d.toISOString().slice(0, 7);
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    return { value, label: label.charAt(0).toUpperCase() + label.slice(1) };
  });

  const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : "—";
  const expired = (d) => d ? new Date(d) < new Date() : false;
  const getUserStatus = (u) => {
    const staffEntry = getStaffEntry(u.email);
    if (ADMIN_EMAILS.includes(u.email) || staffEntry?.role === 'admin' || (u.is_admin && !staffEntry)) {
      return { label: "Admin", cls: "bg-red-500/10 text-red-400" };
    }
    if (staffEntry?.role === 'support') return { label: "Support", cls: "bg-blue-500/10 text-blue-400" };
    if (staffEntry?.role === 'developer') return { label: "Développeur", cls: "bg-violet-500/10 text-violet-300" };
    const hasBots = allBots.some(b => b.user_id === u.id);
    return hasBots ? { label: "Client", cls: "bg-emerald-500/10 text-emerald-400" } : { label: "Membre", cls: "bg-white/5 text-white/40" };
  };

  const activeCount = allBots.filter(b => b.expires_at && !expired(b.expires_at)).length;
  const filteredBots = allBots.filter(b => b.bot_name?.toLowerCase().includes(searchQuery.toLowerCase()) || b.user_email?.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredUsers = allUsers
    .filter(u => {
      const q = userSearchQuery.toLowerCase();
      return !q || u.email?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q) || u.discord_id?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      switch (userSort) {
        case "name-asc": return (a.username || "").localeCompare(b.username || "");
        case "name-desc": return (b.username || "").localeCompare(a.username || "");
        case "date-asc": return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case "date-desc":
        default: return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });

  // === HANDLERS ===
  const genApiKey = async () => {
    try {
      const r = await axios.post(`${API}/admin/api-keys/generate`, {}, { headers: hdr() });
      setApiKeys({ api_key: r.data.api_key });
      toast.success("Clé API universelle générée");
    } catch { toast.error("Erreur"); }
  };

  const addFreeBot = async () => {
    if (!freeBotForm.userId || !freeBotForm.botType) { toast.error("Champs requis"); return; }
    if (freeBotForm.botType.startsWith("vai:")) {
      // Vanta AI : actif tout de suite (pas d'offre à accepter), en jours.
      const days = freeBotForm.duration === "lifetime" ? 3650 : Number(freeBotForm.duration) * 30;
      try {
        const r = await axios.post(`${API}/admin/vai/grant`, { user_id: freeBotForm.userId, plan: freeBotForm.botType.slice(4), days }, { headers: hdr() });
        toast.success(`Vanta AI ${r.data.plan.name} offert à ${freeBotForm.userLabel}`); setShowAddFreeBot(false);
        setFreeBotForm({ userId: "", userLabel: "", botType: "", duration: "1" });
      } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
      return;
    }
    try {
      await axios.post(`${API}/admin/free-bot`, {
        userId: freeBotForm.userId, botType: freeBotForm.botType, duration: freeBotForm.duration,
      }, { headers: hdr() });
      toast.success("Offre envoyée à l'utilisateur"); setShowAddFreeBot(false);
      setFreeBotForm({ userId: "", userLabel: "", botType: "", duration: "1" }); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const updateDuration = async (botId, months) => {
    try { await axios.put(`${API}/admin/bots/${botId}/duration`, { duration_months: months }, { headers: hdr() }); toast.success("Durée mise à jour"); fetchAll(); } catch { toast.error("Erreur"); }
  };

  const removeBot = async () => {
    if (!deleteItem) return;
    try { await axios.delete(`${API}/admin/bots/${deleteItem.id}`, { headers: hdr() }); setAllBots(allBots.filter(b => b.id !== deleteItem.id)); toast.success("Supprimé"); } catch { toast.error("Erreur"); }
    finally { setDeleteItem(null); }
  };

  const transferBot = async () => {
    if (!showTransfer || !transferForm.new_discord_id || !transferForm.new_discord_token) { toast.error("Champs requis"); return; }
    try { await axios.put(`${API}/admin/bots/${showTransfer.id}/transfer`, transferForm, { headers: hdr() }); toast.success("Produit transféré"); setShowTransfer(null); setTransferForm({ new_discord_id: "", new_discord_token: "" }); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const linkDiscord = async () => {
    const id = linkDiscordId.trim();
    if (!/^\d{15,25}$/.test(id)) { toast.error("ID Discord invalide (15 à 25 chiffres)"); return; }
    try {
      await axios.put(`${API}/admin/bots/${showLinkDiscord.id}/discord`, { discord_id: id }, { headers: hdr() });
      toast.success("ID Discord relié : le client sera reconnu au prochain passage du bot");
      setShowLinkDiscord(null); setLinkDiscordId(""); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const regenerateLicense = async () => {
    if (!showLicense) return;
    try {
      const r = await axios.post(`${API}/admin/bots/${showLicense.id}/license/regenerate`, {}, { headers: hdr() });
      setShowLicense({ ...showLicense, license_key: r.data.license_key, license_device_id: null, license_activated_at: null, license_active: true });
      setAllBots(allBots.map(b => b.id === showLicense.id ? { ...b, license_key: r.data.license_key, license_device_id: null, license_activated_at: null, license_active: true } : b));
      toast.success("Nouvelle clé générée, l'ancienne ne fonctionne plus");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleLicense = async () => {
    if (!showLicense) return;
    const active = !showLicense.license_active;
    try {
      await axios.put(`${API}/admin/bots/${showLicense.id}/license/toggle`, { active }, { headers: hdr() });
      setShowLicense({ ...showLicense, license_active: active });
      setAllBots(allBots.map(b => b.id === showLicense.id ? { ...b, license_active: active } : b));
      toast.success(active ? "Clé réactivée" : "Clé désactivée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const createPromo = async () => {
    if (!promoForm.code.trim()) { toast.error("Code requis"); return; }
    try {
      const r = await axios.post(`${API}/admin/promo-codes`, promoForm, { headers: hdr() });
      setPromoCodes([...promoCodes, r.data.promo]); setShowAddPromo(false);
      setPromoForm({ code: "", discount_type: "percentage", discount_value: 10, applies_to: "all", visibility: "private", max_uses: 0, max_uses_per_user: 1, expires_at: "" });
      toast.success("Code promo créé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const togglePromo = async (id, active) => {
    try { await axios.put(`${API}/admin/promo-codes/${id}`, { active: !active }, { headers: hdr() }); setPromoCodes(promoCodes.map(p => p.id === id ? { ...p, active: !active } : p)); } catch { toast.error("Erreur"); }
  };

  const deletePromo = async (id) => {
    try { await axios.delete(`${API}/admin/promo-codes/${id}`, { headers: hdr() }); setPromoCodes(promoCodes.filter(p => p.id !== id)); toast.success("Supprimé"); } catch { toast.error("Erreur"); }
  };

  // SHOP HANDLERS
  const createShopBot = async () => {
    if (!newShopBot.id || !newShopBot.name) { toast.error("ID et nom requis"); return; }
    if (newShopBot.plans.length === 0) { toast.error("Ajoutez au moins un plan"); return; }
    if (plansHaveBadPrice(newShopBot.plans)) { toast.error("Un plan a un prix vide ou invalide"); return; }
    try {
      const payload = { ...newShopBot, features: newShopBot.features_text.split("\n").map(value => value.trim()).filter(Boolean), demo_images: newShopBot.demo_images.filter(Boolean).map(url => ({ url })) };
      const r = await axios.post(`${API}/admin/shop/bots`, payload, { headers: hdr() });
      setShopBots([...shopBots, r.data.bot]); setShowAddShopBot(false);
      setNewShopBot({ id: "", name: "", color: "#5865F2", image_url: "", banner_url: "", description: "", features_text: "", demo_images: ["", "", ""], plans: [], downloadable: false, download_url: "", download_mode: "link", download_filename: "" });
      toast.success("Produit ajouté à la boutique");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const updateShopBot = async () => {
    if (!showEditShopBot) return;
    if (plansHaveBadPrice(showEditShopBot.plans)) { toast.error("Un plan a un prix vide ou invalide"); return; }
    try {
      const payload = { ...showEditShopBot, features: (showEditShopBot.features_text || showEditShopBot.features || "").toString().split("\n").map(value => value.trim()).filter(Boolean), demo_images: (showEditShopBot.demo_images || []).filter(image => image?.url || image).map(image => typeof image === "string" ? { url: image } : image) };
      await axios.put(`${API}/admin/shop/bots/${showEditShopBot.id}`, payload, { headers: hdr() });
      setShopBots(shopBots.map(b => b.id === showEditShopBot.id ? { ...showEditShopBot, ...payload } : b));
      setShowEditShopBot(null); toast.success("Produit mis à jour");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteShopBot = async (id) => {
    try { await axios.delete(`${API}/admin/shop/bots/${id}`, { headers: hdr() }); setShopBots(shopBots.filter(b => b.id !== id)); toast.success("Supprimé de la boutique"); } catch { toast.error("Erreur"); }
  };

  const createAnnouncement = async () => {
    if (!annForm.title.trim()) { toast.error("Titre requis"); return; }
    try {
      const r = await axios.post(`${API}/admin/announcements`, annForm, { headers: hdr() });
      setAnnouncements([r.data.announcement, ...announcements]);
      setShowAddAnnouncement(false); setAnnForm({ title: "", content: "", image_url: "" });
      toast.success("Annonce publiée");
    } catch { toast.error("Erreur"); }
  };
  const deleteAnnouncement = async (id) => {
    try { await axios.delete(`${API}/admin/announcements/${id}`, { headers: hdr() }); setAnnouncements(announcements.filter(a => a.id !== id)); toast.success("Supprimée"); } catch { toast.error("Erreur"); }
  };
  const replyTicket = async (ticketId) => {
    if (!replyText.trim()) return;
    try { await axios.put(`${API}/admin/support/tickets/${ticketId}`, { reply: replyText }, { headers: hdr() }); setReplyText(""); fetchAll(); toast.success("Réponse envoyée"); } catch { toast.error("Erreur"); }
  };
  const updateTicketStatus = async (ticketId, status) => {
    try { await axios.put(`${API}/admin/support/tickets/${ticketId}`, { status }, { headers: hdr() }); setTickets(tickets.map(t => t.id === ticketId ? { ...t, status } : t)); toast.success("Mis à jour"); } catch { toast.error("Erreur"); }
  };

  const deleteTicket = async (ticketId) => {
    try { await axios.delete(`${API}/admin/support/tickets/${ticketId}`, { headers: hdr() }); setTickets(tickets.filter(t => t.id !== ticketId)); toast.success("Ticket supprimé"); } catch { toast.error("Erreur"); }
  };

  const deleteReview = async (id) => {
    try { await axios.delete(`${API}/admin/reviews/${id}`, { headers: hdr() }); setReviews(reviews.filter(r => r.id !== id)); toast.success("Avis supprimé"); } catch { toast.error("Erreur"); }
  };

  const getStaffEntry = (email) => staffList.find(s => s.email === email);

  const setUserStaffRole = async (targetUser, role) => {
    const existing = getStaffEntry(targetUser.email);
    try {
      if (role === "none") {
        if (existing) {
          await axios.delete(`${API}/admin/staff/${existing.id}`, { headers: hdr() });
          setStaffList(staffList.filter(s => s.id !== existing.id));
          toast.success("Staff retiré");
        }
      } else if (existing) {
        await axios.put(`${API}/admin/staff/${existing.id}`, { role }, { headers: hdr() });
        setStaffList(staffList.map(s => (s.id === existing.id ? { ...s, role } : s)));
        toast.success("Rôle mis à jour");
      } else {
        const r = await axios.post(`${API}/admin/staff`, { email: targetUser.email, role }, { headers: hdr() });
        setStaffList([...staffList, r.data.staff]);
        toast.success("Staff ajouté");
      }
      setStaffDialogUser(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openUserSubscriptions = (u) => { setSearchQuery(u.email); setTab("subscriptions"); };

  const openFreeBotForUser = (u) => {
    setFreeBotForm(f => ({ ...f, userId: u.id, userLabel: u.username || u.email }));
    setShowAddFreeBot(true);
  };

  const impersonateUser = async (u) => {
    try {
      const r = await axios.post(`${API}/admin/users/${u.id}/impersonate`, {}, { headers: hdr() });
      const url = `${window.location.origin}/?impersonate_token=${encodeURIComponent(r.data.access_token)}`;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'accéder à ce compte");
    }
  };

  const saveSiteSettings = async () => {
    try {
      await axios.put(`${API}/admin/site-settings`, {
        maintenance_mode: siteSettings.maintenance_mode,
        maintenance_message: siteSettings.maintenance_message || "",
      }, { headers: hdr() });
      toast.success(siteSettings.maintenance_mode ? "Mode maintenance activé" : "Paramètres enregistrés");
    } catch { toast.error("Erreur"); }
  };

  const updateShopBotStatus = async (botId, status) => {
    try {
      await axios.put(`${API}/admin/shop/bots/${botId}`, { status }, { headers: hdr() });
      setShopBots(shopBots.map(b => b.id === botId ? { ...b, status } : b));
      toast.success("Statut mis à jour");
    } catch { toast.error("Erreur"); }
  };


  const buildPlan = () => {
    if (!newPlan.name) { toast.error("Nom du plan requis"); return null; }
    const planId = newPlan.name.toLowerCase().replace(/\s/g, '-');
    let days = newPlan.is_lifetime ? 0 : (newPlan.duration_unit === "months" ? newPlan.duration_days * 30 : newPlan.duration_days);
    let label = newPlan.is_lifetime ? "A vie" : newPlan.duration_label;
    return { id: planId, name: newPlan.name, price: newPlan.price, duration_days: days, duration_label: label };
  };

  // Prix d'un plan déjà ajouté, modifiable directement dans la liste.
  const withPlanPrice = (plans, index, raw) =>
    plans.map((p, idx) => (idx === index ? { ...p, price: raw === "" ? "" : Math.max(0, parseFloat(raw) || 0) } : p));
  const plansHaveBadPrice = (plans) => (plans || []).some((p) => p.price === "" || !Number.isFinite(Number(p.price)) || Number(p.price) < 0);

  const resetPlan = () => setNewPlan({ name: "", price: 2, duration_days: 30, duration_label: "1 mois", duration_unit: "months", is_lifetime: false });

  const addPlanToNew = () => {
    const plan = buildPlan();
    if (!plan) return;
    setNewShopBot({ ...newShopBot, plans: [...newShopBot.plans, plan] });
    resetPlan();
  };

  const addPlanToEdit = () => {
    if (!showEditShopBot) return;
    const plan = buildPlan();
    if (!plan) return;
    setShowEditShopBot({ ...showEditShopBot, plans: [...(showEditShopBot.plans || []), plan] });
    resetPlan();
  };

  if (loading) return <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;
  if (!user || (!user.is_admin && !ADMIN_EMAILS.includes(user.email) && !userRole)) return null;

  const navBadges = {
    tickets: tickets.filter((t) => t.status === "open").length,
    alerts: openAlerts,
  };
  const roleLabel = ADMIN_EMAILS.includes(user.email) ? "Fondateur" : userRole === "support" ? "Support" : userRole === "developer" ? "Développeur" : "Administrateur";

  const tabBadge = (t) => (t.badge ? navBadges[t.badge] || 0 : 0);
  const badgeClass = (kind) => (kind === "alerts" ? "bg-red-500/20 text-red-300" : "bg-blue-500/20 text-blue-300");
  const openSection = (section) => {
    const remembered = lastTabBySection.current[section.id];
    setTab(section.tabs.some((t) => t.id === remembered) ? remembered : section.tabs[0].id);
  };

  const renderAdminNav = (onNavigate, testidPrefix = "admin-nav") => (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-3">
        <div className="relative overflow-hidden flex items-center gap-3 rounded-2xl bg-gradient-to-br from-blue-600/[0.14] to-violet-600/[0.1] border border-white/[0.07] px-3 py-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shrink-0 shadow-lg shadow-blue-900/40">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight">Panel admin</p>
            <p className="text-[11px] text-white/40 truncate">{roleLabel}</p>
          </div>
        </div>
      </div>
      <p className="px-6 pb-2 pt-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-white/25">Navigation</p>
      <nav className="flex-1 px-3 pb-3 space-y-0.5" data-testid={`${testidPrefix}-list`}>
        {navSections.map((section) => {
          const active = currentSection?.id === section.id;
          const alertTab = section.tabs.find((t) => tabBadge(t) > 0);
          const count = section.tabs.reduce((sum, t) => sum + tabBadge(t), 0);
          return (
            <button
              key={section.id}
              data-testid={`${testidPrefix}-${section.id}`}
              onClick={() => { openSection(section); onNavigate && onNavigate(); }}
              className={`group relative w-full flex items-center gap-3 pl-3 pr-2.5 py-2 text-left rounded-xl transition-colors ${active ? "bg-white/[0.07]" : "hover:bg-white/[0.035]"}`}
            >
              {active && <span className="absolute left-0 top-1/2 h-6 -translate-y-1/2 w-[3px] rounded-r-full bg-gradient-to-b from-blue-400 to-violet-500" />}
              <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0 ring-1 ring-inset transition-transform group-hover:scale-105 ${active ? "bg-gradient-to-br from-blue-500/25 to-violet-500/20 ring-blue-400/25" : "bg-white/[0.03] ring-white/[0.05]"}`}>
                <section.icon className={`w-4 h-4 ${active ? "text-blue-200" : "text-white/45"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] leading-tight truncate ${active ? "text-white font-medium" : "text-white/70"}`}>{section.label}</p>
                <p className="text-[11px] text-white/30 truncate mt-0.5">{section.tabs.length > 1 ? section.hint : section.tabs[0].label}</p>
              </div>
              {count > 0 && (
                <span data-testid={`${testidPrefix}-${section.id}-badge`} className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold flex items-center justify-center shrink-0 ${badgeClass(alertTab?.badge)}`}>
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="px-3 py-3 border-t border-white/5">
        <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-3 py-2 text-[13px] text-white/45 hover:text-white hover:bg-white/[0.04] rounded-lg transition-colors">
          <ArrowLeft className="w-4 h-4" />Retour au site
        </button>
      </div>
    </div>
  );

  // En-tête de la rubrique : son nom, puis ses onglets (masqués s'il n'y en a qu'un).
  const renderSectionHeader = () => currentSection && (
    <div className="mb-6" data-testid="admin-section-header">
      <div key={currentSection.id} className="page-rise flex items-center gap-3.5 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 ring-1 ring-inset ring-white/10 flex items-center justify-center shrink-0">
          <currentSection.icon className="w-5 h-5 text-blue-200" />
        </div>
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight leading-tight">{currentSection.label}</h1>
          {currentSection.hint && <p className="text-sm text-white/40 truncate">{currentSection.hint}</p>}
        </div>
      </div>
      {currentSection.tabs.length > 1 && (
        <div className="inline-flex max-w-full gap-1 overflow-x-auto rounded-xl border border-white/[0.07] bg-white/[0.025] p-1" role="tablist">
          {currentSection.tabs.map((t) => {
            const active = t.id === tab;
            const count = tabBadge(t);
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                data-testid={`admin-subtab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13px] whitespace-nowrap transition-all ${active ? "bg-white/10 text-white font-medium shadow-sm" : "text-white/45 hover:text-white/80"}`}
              >
                <t.icon className={`w-4 h-4 ${active ? "text-blue-300" : "text-white/30"}`} />
                {t.label}
                {count > 0 && (
                  <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center ${badgeClass(t.badge)}`}>
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />
      <div className="pt-16 flex min-h-screen">
        {/* Desktop : menu latéral fixe (inchangé) */}
        <aside className="hidden lg:block w-64 bg-[#0b0c14]/95 backdrop-blur-xl border-r border-white/[0.06] fixed left-0 top-16 bottom-0 overflow-y-auto z-10" data-testid="admin-sidebar">
          {renderAdminNav()}
        </aside>

        {/* Mobile / tablette : menu en tiroir */}
        {adminSidebarOpen && (
          <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setAdminSidebarOpen(false)} />
        )}
        <aside className={`fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-[#0c0c14] border-r border-white/5 z-50 overflow-y-auto transition-transform duration-200 lg:hidden ${adminSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <button data-testid="admin-mobile-sidebar-close" onClick={() => setAdminSidebarOpen(false)} className="absolute top-5 right-5 p-1.5 hover:bg-white/5 rounded transition-colors z-10">
            <X className="w-5 h-5 text-white/50" />
          </button>
          {renderAdminNav(() => setAdminSidebarOpen(false), "admin-nav-mobile")}
        </aside>

        <main className="lg:ml-64 flex-1 min-w-0 p-4 lg:p-8 max-w-6xl">
          <button data-testid="admin-mobile-sidebar-open" onClick={() => setAdminSidebarOpen(true)} className="lg:hidden flex items-center gap-2 mb-4 px-3 py-2 rounded-2xl bg-[#0f1019] border border-white/[0.07] text-sm text-white/60 hover:text-white">
            <Menu className="w-4 h-4" />Menu
          </button>

          {twoFARequired && (
            <div className="mb-6 flex items-center justify-between gap-4 rounded-lg border border-red-500/30 bg-red-950/20 p-4">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-red-400 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-red-300">Double authentification requise</p>
                  <p className="text-xs text-white/50 mt-0.5">
                    Votre compte n'a pas la 2FA activée : le serveur bloque toutes les données de l'admin (c'est pour ça que tout semble vide).
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/settings?tab=security')} className="bg-red-600 hover:bg-red-500 rounded-lg text-sm shrink-0">Activer la 2FA</Button>
            </div>
          )}


          {renderSectionHeader()}

          {tab === "product-stats" && (
            <div>
              <h2 className="text-lg font-semibold mb-1">Statistiques par produit</h2>
              <p className="text-white/40 text-sm mb-6">Ventes, chiffre d'affaires, clients actifs, essais gratuits et codes promo, produit par produit.</p>
              <ProductStatsPanel />
            </div>
          )}

          {tab === "licenses" && (
            <div>
              <h2 className="text-lg font-semibold mb-1">Clés de licence</h2>
              <p className="text-white/40 text-sm mb-6">Serveur lié à chaque clé, dernier contact et tentatives depuis d'autres serveurs.</p>
              <LicensesPanel />
            </div>
          )}

          {tab === "trials" && (
            <div>
              <h2 className="text-lg font-semibold mb-1">Essais gratuits</h2>
              <p className="text-white/40 text-sm mb-6">Propose un essai gratuit sur n'importe quel produit (script ou bot Discord), choisis sa durée, retire-le quand tu veux, et suis les essais en cours.</p>
              <TrialsPanel />
            </div>
          )}

          {tab === "protection" && (
            <div>
              <h2 className="text-lg font-semibold mb-1">Protection anti-fuite</h2>
              <p className="text-white/40 text-sm mb-6">Alertes quand une clé est partagée ou désactivée mais toujours utilisée, traçage d'une copie fuitée grâce au filigrane invisible, et webhook Discord.</p>
              <ProtectionPanel />
            </div>
          )}

          {/* OVERVIEW */}
          {tab === "overview" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h2 className="text-lg font-semibold">Vue d'ensemble</h2>
                {canManageAnnouncements && <Button data-testid="quick-create-announcement" onClick={() => setShowAddAnnouncement(true)} className="bg-blue-600 hover:bg-blue-500 rounded-lg text-sm"><Plus className="w-4 h-4 mr-1.5" />Créer une annonce</Button>}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { label: "Utilisateurs", value: allUsers.length, icon: Users, tint: "from-blue-500/20 to-blue-500/5 text-blue-300", glow: "bg-blue-500/20" },
                  { label: "Produits actifs", value: activeCount, icon: Bot, tint: "from-emerald-500/20 to-emerald-500/5 text-emerald-300", glow: "bg-emerald-500/20" },
                  { label: "Boutique", value: shopBots.length, icon: Store, tint: "from-amber-500/20 to-amber-500/5 text-amber-300", glow: "bg-amber-500/20" },
                  { label: "Codes Promo", value: promoCodes.length, icon: Tag, tint: "from-violet-500/20 to-violet-500/5 text-violet-300", glow: "bg-violet-500/20" },
                ].map((s, index) => (
                  <div key={s.label} className="page-rise relative overflow-hidden bg-[#0f1019] border border-white/[0.07] p-5 rounded-2xl" style={{ animationDelay: `${index * 50}ms` }}>
                    <div className={`pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl ${s.glow}`} />
                    <div className="relative flex items-start justify-between">
                      <p className="text-white/45 text-xs uppercase tracking-wider">{s.label}</p>
                      <span className={`flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ring-1 ring-inset ring-white/10 ${s.tint}`}><s.icon className="h-4 w-4" /></span>
                    </div>
                    <p className="relative mt-2 font-heading text-3xl font-bold">{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5 mb-8">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h2 className="font-semibold text-white/70 flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-400" />Revenus</h2>
                  <div className="flex items-center gap-2">
                    <Select value={revenueMonth} onValueChange={setRevenueMonth}>
                      <SelectTrigger className="bg-[#0a0a0f] border-white/10 rounded-lg w-40 text-sm h-8"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#12121a] border-white/10">
                        {revenueMonthOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button onClick={exportRevenueRecap} variant="outline" size="sm" className="border-white/10 text-sm gap-1.5 h-8">
                      <Download className="w-3.5 h-3.5" /> Télécharger le récap
                    </Button>
                  </div>
                </div>

                {revenueLoading ? (
                  <p className="text-white/30 text-sm py-4">Chargement...</p>
                ) : !revenueStats ? (
                  <p className="text-white/30 text-sm py-4">Aucune donnée disponible</p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Chiffre d'affaires</p>
                        <p className="text-2xl font-bold text-green-400">{revenueStats.total_revenue.toFixed(2)}€</p>
                        {revenueStats.growth_pct !== null && (
                          <p className={`text-xs mt-1 ${revenueStats.growth_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {revenueStats.growth_pct >= 0 ? '+' : ''}{revenueStats.growth_pct}% vs mois précédent
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Ventes</p>
                        <p className="text-2xl font-bold">{revenueStats.transaction_count}</p>
                      </div>
                      <div>
                        <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Mois précédent</p>
                        <p className="text-2xl font-bold text-white/50">{revenueStats.previous_month_revenue.toFixed(2)}€</p>
                      </div>
                    </div>

                    {revenueStats.top_products.length > 0 && (
                      <div className="border-t border-white/5 pt-4">
                        <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Top produits</p>
                        {revenueStats.top_products.slice(0, 5).map(p => (
                          <div key={p.bot_type} className="flex items-center justify-between py-1.5 text-sm">
                            <span className="text-white/70">{p.label}</span>
                            <span className="text-white/40">{p.count} vente{p.count > 1 ? 's' : ''} · <span className="text-white">{p.revenue.toFixed(2)}€</span></span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-5">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="font-semibold text-white/70">Derniers achats</h2>
                  {hasAccess("subscriptions") && allBots.length > 0 && (
                    <button data-testid="overview-see-subscriptions" onClick={() => { setSearchQuery(""); setTab("subscriptions"); }} className="text-xs text-blue-400 hover:text-blue-300">Voir tous les abonnements →</button>
                  )}
                </div>
                {allBots.slice(0, 5).map((b) => (
                  <div key={b.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${expired(b.expires_at) ? 'bg-red-500' : 'bg-green-500'}`} />
                      <div><p className="text-sm font-medium">{b.bot_name}</p><p className="text-xs text-white/30">{b.user_email || '—'}</p></div>
                    </div>
                    <span className={`text-xs ${expired(b.expires_at) ? 'text-red-400' : 'text-green-400'}`}>{expired(b.expires_at) ? 'Expiré' : 'Actif'}</span>
                  </div>
                ))}
                {allBots.length === 0 && <p className="text-white/30 text-sm">Aucun produit</p>}
              </div>
            </div>
          )}

          {/* BOUTIQUE */}
          {tab === "boutique" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                  <h2 className="text-lg font-semibold">Produits en vente</h2>
                  <p className="text-white/40 text-sm mt-1">Gérez les produits en vente, leurs prix et durées. Tout apparaît dans le menu Tarifs.</p>
                </div>
                <Button onClick={() => setShowAddShopBot(true)} className="bg-blue-600 hover:bg-blue-700 rounded-lg text-sm">
                  <Plus className="w-4 h-4 mr-1.5" />Ajouter un produit
                </Button>
              </div>

              {shopBots.length === 0 ? (
                <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-12 text-center">
                  <Store className="w-10 h-10 text-white/10 mx-auto mb-3" />
                  <p className="text-white/30 mb-2">Aucun produit dans la boutique</p>
                  <p className="text-white/20 text-sm">Ajoutez votre premier produit pour qu'il apparaisse dans les tarifs</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {shopBots.map(bot => (
                    <div key={bot.id} className="bg-[#0f1019] border border-white/[0.07] rounded-2xl overflow-hidden">
                      {/* Bot header */}
                      <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedBot(expandedBot === bot.id ? null : bot.id)}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: `${bot.color}20` }}>
                            {bot.image_url ? <img src={bot.image_url} alt="" className="w-full h-full object-cover" /> : <Bot className="w-5 h-5" style={{ color: bot.color }} />}
                          </div>
                          <div>
                            <p className="font-semibold">{bot.name}</p>
                            <p className="text-xs text-white/40">{(bot.plans || []).length} plan(s) — {bot.active ? 'Actif' : 'Inactif'}{bot.current_version ? ` — v${bot.current_version}` : ''}{bot.trial_days > 0 ? ` — essai ${bot.trial_days} j` : ''}{bot.includes?.length ? ` — pack de ${bot.includes.length}` : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {bot.downloadable && <Button data-testid={`release-${bot.id}`} size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setReleaseBot(bot); }} className="text-white/40 hover:text-white h-7 w-7 p-0" title="Publier une nouvelle version"><Upload className="w-3.5 h-3.5" /></Button>}
                          <Button data-testid={`customize-page-${bot.id}`} size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setBuilderBot(bot); }} className="text-white/40 hover:text-white h-7 w-7 p-0" title="Personnaliser la page"><LayoutTemplate className="w-3.5 h-3.5" /></Button>
                          <Button data-testid={`edit-shop-${bot.id}`} size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setShowEditShopBot({...bot}); }} className="text-white/40 hover:text-white h-7 w-7 p-0"><Edit className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deleteShopBot(bot.id); }} className="text-red-400 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                          {expandedBot === bot.id ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                        </div>
                      </div>
                      {/* Plans list */}
                      {expandedBot === bot.id && (
                        <div className="border-t border-white/5 p-4">
                          <div className="grid grid-cols-4 gap-3 mb-2 text-xs text-white/40 uppercase tracking-wider px-2">
                            <span>Plan</span><span>Prix</span><span>Durée</span><span>Label</span>
                          </div>
                          {(bot.plans || []).map((p) => (
                            <div key={p.id || p.name} className="grid grid-cols-4 gap-3 p-2 rounded hover:bg-white/[0.02] text-sm items-center">
                              <span className="font-medium">{p.name}</span>
                              <span style={{ color: bot.color }}>{p.price}€</span>
                              <span className="text-white/50">{p.duration_days === 0 ? 'A vie' : `${p.duration_days}j`}</span>
                              <span className="text-white/40">{p.duration_label}</span>
                            </div>
                          ))}
                          {(bot.plans || []).length === 0 && <p className="text-white/20 text-sm p-2">Aucun plan</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SUBSCRIPTIONS */}
          {tab === "subscriptions" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                  <h2 className="text-lg font-semibold">Abonnements ({filteredBots.length}/{allBots.length})</h2>
                  <p className="text-white/40 text-sm mt-1">Tous les produits achetés ou offerts, avec leur date d'expiration.</p>
                </div>
                <Button onClick={() => exportCsv("subscriptions")} variant="outline" className="border-white/10 text-sm gap-2">
                  <Download className="w-4 h-4" /> Export abonnements
                </Button>
              </div>
              <div className="mb-4 relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input data-testid="subscriptions-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Rechercher un produit ou un email..." className="bg-[#12121a] border-white/10 pl-10 pr-9 rounded-lg text-sm" />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-white/30 hover:text-white" title="Effacer la recherche"><X className="w-3.5 h-3.5" /></button>
                )}
              </div>
              {/* Desktop : tableau (inchangé) */}
              <div className="hidden lg:block bg-[#0f1019] border border-white/[0.07] rounded-2xl overflow-hidden">
                <div className="grid grid-cols-6 gap-4 p-3 border-b border-white/5 text-xs text-white/40 uppercase tracking-wider">
                  <span>Nom</span><span>Produit</span><span>Utilisateur</span><span>Statut</span><span>Expire</span><span>Actions</span>
                </div>
                {filteredBots.map(b => (
                  <div key={b.id} className="grid grid-cols-6 gap-4 p-3 border-b border-white/5 items-center text-sm hover:bg-white/[0.02]">
                    <span className="truncate font-medium">{b.bot_name}</span>
                    <span className="text-white/50">{b.bot_type}</span>
                    <span className="text-white/40 truncate">{b.user_email || '—'}</span>
                    <span className={expired(b.expires_at) ? 'text-red-400' : 'text-green-400'}>{expired(b.expires_at) ? 'Expiré' : 'Actif'}</span>
                    <span className="text-white/50">{fmt(b.expires_at)}</span>
                    <div className="flex gap-1">
                      {isFullAdmin ? (
                        <>
                          {b.downloadable && (
                            <Button size="sm" variant="ghost" onClick={() => setShowLicense(b)} className="h-7 w-7 p-0 text-yellow-400" title="Licence" data-testid={`license-${b.id}`}><Key className="w-3 h-3" /></Button>
                          )}
                          {!b.downloadable && (
                            <Button size="sm" variant="ghost" onClick={() => { setShowLinkDiscord(b); setLinkDiscordId(b.discord_id || ""); }} className="h-7 w-7 p-0 text-[#8791f0]" title="Relier un ID Discord" data-testid={`link-discord-${b.id}`}><Link2 className="w-3 h-3" /></Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setShowTransfer(b)} className="h-7 w-7 p-0 text-blue-400" title="Transférer"><ArrowRightLeft className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setShowEditBot(b)} className="h-7 w-7 p-0 text-white/40" title="Durée"><Edit className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setDeleteItem(b)} className="h-7 w-7 p-0 text-red-400" title="Supprimer"><Trash2 className="w-3 h-3" /></Button>
                        </>
                      ) : (
                        <span className="text-white/20 text-xs">Lecture seule</span>
                      )}
                    </div>
                  </div>
                ))}
                {filteredBots.length === 0 && <p className="p-6 text-center text-white/30 text-sm">Aucun abonnement</p>}
              </div>

              {/* Mobile / tablette : cartes empilées */}
              <div className="lg:hidden space-y-3">
                {filteredBots.map(b => (
                  <div key={b.id} className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{b.bot_name}</p>
                        <p className="text-white/50 text-sm">{b.bot_type}</p>
                      </div>
                      <span className={`text-xs shrink-0 ${expired(b.expires_at) ? 'text-red-400' : 'text-green-400'}`}>{expired(b.expires_at) ? 'Expiré' : 'Actif'}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs text-white/40 mb-3">
                      <span className="truncate">{b.user_email || '—'}</span>
                      <span className="shrink-0">{fmt(b.expires_at)}</span>
                    </div>
                    <div className="flex gap-1 pt-3 border-t border-white/5">
                      {isFullAdmin ? (
                        <>
                          {b.downloadable && (
                            <Button size="sm" variant="ghost" onClick={() => setShowLicense(b)} className="h-8 w-8 p-0 text-yellow-400" title="Licence" data-testid={`license-m-${b.id}`}><Key className="w-4 h-4" /></Button>
                          )}
                          {!b.downloadable && (
                            <Button size="sm" variant="ghost" onClick={() => { setShowLinkDiscord(b); setLinkDiscordId(b.discord_id || ""); }} className="h-8 w-8 p-0 text-[#8791f0]" title="Relier un ID Discord" data-testid={`link-discord-m-${b.id}`}><Link2 className="w-4 h-4" /></Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setShowTransfer(b)} className="h-8 w-8 p-0 text-blue-400" title="Transférer"><ArrowRightLeft className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setShowEditBot(b)} className="h-8 w-8 p-0 text-white/40" title="Durée"><Edit className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setDeleteItem(b)} className="h-8 w-8 p-0 text-red-400" title="Supprimer"><Trash2 className="w-4 h-4" /></Button>
                        </>
                      ) : (
                        <span className="text-white/20 text-xs">Lecture seule</span>
                      )}
                    </div>
                  </div>
                ))}
                {filteredBots.length === 0 && <p className="p-6 text-center text-white/30 text-sm">Aucun abonnement</p>}
              </div>
            </div>
          )}

          {/* USERS */}
          {tab === "users" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h2 className="text-lg font-semibold">Utilisateurs ({filteredUsers.length}/{allUsers.length})</h2>
                <Button onClick={() => exportCsv("users")} variant="outline" className="border-white/10 text-sm gap-2">
                  <Download className="w-4 h-4" /> Export utilisateurs
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative max-w-sm flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    value={userSearchQuery}
                    onChange={e => setUserSearchQuery(e.target.value)}
                    placeholder="Rechercher un pseudo, email ou ID Discord..."
                    className="bg-[#12121a] border-white/10 pl-10 rounded-lg text-sm"
                  />
                </div>
                <Select value={userSort} onValueChange={setUserSort}>
                  <SelectTrigger className="bg-[#12121a] border-white/10 rounded-lg w-56 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#12121a] border-white/10">
                    <SelectItem value="date-desc">Plus récents d'abord</SelectItem>
                    <SelectItem value="date-asc">Plus anciens d'abord</SelectItem>
                    <SelectItem value="name-asc">Pseudo (A → Z)</SelectItem>
                    <SelectItem value="name-desc">Pseudo (Z → A)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Desktop : tableau (inchangé) */}
              <div className="hidden lg:block bg-[#0f1019] border border-white/[0.07] rounded-2xl overflow-hidden">
                <div className="grid grid-cols-[1.1fr_1.3fr_0.9fr_0.8fr_0.7fr_1.3fr] gap-4 p-3 border-b border-white/5 text-xs text-white/40 uppercase tracking-wider">
                  <span>Nom</span><span>Email</span><span>Discord</span><span>Inscrit</span><span>Statut</span><span className="text-right">Actions</span>
                </div>
                {filteredUsers.map(u => {
                  const status = getUserStatus(u);
                  return (
                  <div key={u.id} className="grid grid-cols-[1.1fr_1.3fr_0.9fr_0.8fr_0.7fr_1.3fr] gap-4 p-3 border-b border-white/5 items-center text-sm hover:bg-white/[0.02]">
                    <span className="font-medium truncate">{u.username}</span>
                    <span className="text-white/50 truncate">{u.email}</span>
                    <span className="text-white/40 font-mono text-xs">
                      {u.discord_id ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#5865F2]/10 text-[#8791f0]" title={u.discord_username || u.discord_id}>
                          {u.discord_id}
                        </span>
                      ) : "—"}
                    </span>
                    <span className="text-white/40">{fmt(u.created_at)}</span>
                    <span><span className={`text-xs px-2 py-0.5 rounded ${status.cls}`}>{status.label}</span></span>
                    <div className="flex items-center gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => openUserSubscriptions(u)} className="h-7 w-7 p-0 text-blue-400" title="Voir ses abonnements" data-testid={`user-subscriptions-${u.id}`}>
                        <CreditCard className="w-3.5 h-3.5" />
                      </Button>
                      {isFullAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => openFreeBotForUser(u)} className="h-7 w-7 p-0 text-green-400" title="Donner gratuit" data-testid={`user-free-bot-${u.id}`}>
                          <Gift className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {isFullAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => impersonateUser(u)} className="h-7 w-7 p-0 text-purple-400" title="Regarder" data-testid={`user-view-as-${u.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {isFullAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => { setStaffDialogUser(u); setStaffDialogRole(getStaffEntry(u.email)?.role || "none"); }} className="h-7 w-7 p-0 text-cyan-400" title="Statut staff" data-testid={`user-staff-${u.id}`}>
                          <UserCog className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  );
                })}
                {filteredUsers.length === 0 && <p className="p-6 text-center text-white/30 text-sm">Aucun utilisateur</p>}
              </div>

              {/* Mobile / tablette : cartes empilées */}
              <div className="lg:hidden space-y-3">
                {filteredUsers.map(u => {
                  const status = getUserStatus(u);
                  return (
                    <div key={u.id} className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{u.username}</p>
                          <p className="text-white/50 text-sm truncate">{u.email}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${status.cls}`}>{status.label}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 text-xs text-white/40 mb-3">
                        {u.discord_id ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#5865F2]/10 text-[#8791f0] font-mono truncate">{u.discord_id}</span>
                        ) : <span>Pas de Discord lié</span>}
                        <span className="shrink-0">{fmt(u.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-1 pt-3 border-t border-white/5">
                        <Button size="sm" variant="ghost" onClick={() => openUserSubscriptions(u)} className="h-8 w-8 p-0 text-blue-400" title="Voir ses abonnements" data-testid={`user-subscriptions-m-${u.id}`}>
                          <CreditCard className="w-4 h-4" />
                        </Button>
                        {isFullAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => openFreeBotForUser(u)} className="h-8 w-8 p-0 text-green-400" title="Donner gratuit" data-testid={`user-free-bot-m-${u.id}`}>
                            <Gift className="w-4 h-4" />
                          </Button>
                        )}
                        {isFullAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => impersonateUser(u)} className="h-8 w-8 p-0 text-purple-400" title="Regarder" data-testid={`user-view-as-m-${u.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        {isFullAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => { setStaffDialogUser(u); setStaffDialogRole(getStaffEntry(u.email)?.role || "none"); }} className="h-8 w-8 p-0 text-cyan-400" title="Statut staff" data-testid={`user-staff-m-${u.id}`}>
                            <UserCog className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {filteredUsers.length === 0 && <p className="p-6 text-center text-white/30 text-sm">Aucun utilisateur</p>}
              </div>
            </div>
          )}

          {/* PROMO CODES */}
          {tab === "promo-codes" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div><h2 className="text-lg font-semibold">Codes promo</h2><p className="text-white/40 text-sm mt-1">Public = visible par tous | Privé = code secret</p></div>
                <Button onClick={() => setShowAddPromo(true)} className="bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"><Plus className="w-4 h-4 mr-1.5" />Créer</Button>
              </div>
              {promoCodes.length === 0 ? (
                <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-12 text-center"><Tag className="w-10 h-10 text-white/10 mx-auto mb-3" /><p className="text-white/30">Aucun code promo</p></div>
              ) : (
                <div className="space-y-3">
                  {promoCodes.map(p => (
                    <div key={p.id} className={`bg-[#12121a] border rounded-lg p-4 flex items-center justify-between ${p.active ? 'border-white/5' : 'border-red-500/20 opacity-50'}`}>
                      <div className="flex items-center gap-4">
                        <code className="font-mono font-bold bg-white/5 px-3 py-1 rounded">{p.code}</code>
                        <span className={`text-xs px-2 py-0.5 rounded ${p.visibility === 'public' ? 'bg-blue-500/20 text-blue-400' : 'bg-white/5 text-white/40'}`}>{p.visibility === 'public' ? 'Public' : 'Privé'}</span>
                        <span className="text-sm text-white/50">{p.discount_type === 'percentage' ? `${p.discount_value}%` : `${p.discount_value}€`}</span>
                        <span className="text-sm text-white/30">{p.applies_to === 'all' ? 'Toute la boutique' : (shopBots.find(b => b.id === p.applies_to)?.name || p.applies_to)}</span>
                        <span className="text-xs text-white/30" title="Commandes validées avec ce code">{p.redemptions || 0} utilisé(s){p.max_uses > 0 ? ` / ${p.max_uses}` : ''}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-white/5 text-white/40">{p.max_uses_per_user > 0 ? `${p.max_uses_per_user}× par personne` : 'Illimité par personne'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => togglePromo(p.id, p.active)} className={`text-xs h-7 ${p.active ? 'text-yellow-400' : 'text-green-400'}`}>{p.active ? 'Désactiver' : 'Activer'}</Button>
                        <Button size="sm" variant="ghost" onClick={() => deletePromo(p.id)} className="text-red-400 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* INFO / ANNOUNCEMENTS */}
          {tab === "info" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h2 className="text-lg font-semibold">Annonces</h2>
                <Button onClick={() => setShowAddAnnouncement(true)} className="bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"><Plus className="w-4 h-4 mr-1.5" />Publier</Button>
              </div>
              {announcements.length === 0 ? (
                <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-12 text-center"><FileText className="w-10 h-10 text-white/10 mx-auto mb-3" /><p className="text-white/30">Aucune annonce</p></div>
              ) : (
                <div className="space-y-3">
                  {announcements.map(a => (
                    <div key={a.id} className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {a.image_url && <img src={a.image_url} alt="" className="w-12 h-12 object-cover rounded" />}
                        <div>
                          <p className="font-medium text-sm">{a.title}</p>
                          <p className="text-xs text-white/30 truncate max-w-sm">{a.content}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => deleteAnnouncement(a.id)} className="text-red-400 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SUPPORT TICKETS */}
          {tab === "support" && (() => {
            const RANKP = { urgent: 0, moyen: 1, faible: 2 };
            const PRIO = {
              urgent: { label: "Urgent", cls: "bg-red-500/15 text-red-300 ring-red-400/25" },
              moyen: { label: "Moyen", cls: "bg-amber-500/15 text-amber-300 ring-amber-400/25" },
              faible: { label: "Faible", cls: "bg-sky-500/15 text-sky-300 ring-sky-400/25" },
            };
            const filtered = tickets
              .filter((t) => (ticketFilter === "all" ? true : t.priority === ticketFilter))
              .filter((t) => !ticketOpenOnly || t.status !== "resolved")
              .sort((a, b) => (a.status === "resolved") - (b.status === "resolved") || (RANKP[a.priority] ?? 1) - (RANKP[b.priority] ?? 1) || String(b.created_at).localeCompare(String(a.created_at)));
            const count = (key) => tickets.filter((t) => t.status !== "resolved" && t.priority === key).length;
            return (
            <div data-testid="admin-tickets">
              <div className="mb-5 grid grid-cols-3 gap-3">
                {[["urgent", "Urgents", "text-red-300"], ["moyen", "Moyens", "text-amber-300"], ["faible", "Faibles", "text-sky-300"]].map(([key, label, color]) => (
                  <button key={key} onClick={() => setTicketFilter(ticketFilter === key ? "all" : key)} className={`rounded-2xl border p-4 text-left transition ${ticketFilter === key ? "border-white/20 bg-white/[0.06]" : "border-white/[0.07] bg-[#0f1019] hover:border-white/15"}`}>
                    <p className="text-xs text-white/45">{label} ouverts</p>
                    <p className={`mt-1 font-heading text-2xl font-bold ${color}`}>{count(key)}</p>
                  </button>
                ))}
              </div>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-white/40 text-sm">{tickets.filter(t => t.status !== 'resolved').length} ticket(s) ouvert(s) · classés par l'IA, les plus urgents en haut</p>
                <label className="flex cursor-pointer items-center gap-2 text-xs text-white/55">
                  <input type="checkbox" checked={ticketOpenOnly} onChange={(e) => setTicketOpenOnly(e.target.checked)} className="accent-blue-500" />Masquer les résolus
                </label>
              </div>
              {filtered.length === 0 ? (
                <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-12 text-center"><Headphones className="w-10 h-10 text-white/10 mx-auto mb-3" /><p className="text-white/30">Aucun ticket</p></div>
              ) : (
                <div className="space-y-2.5">
                  {filtered.map(t => {
                    const prio = PRIO[t.priority] || PRIO.moyen;
                    return (
                    <div key={t.id} data-testid={`admin-ticket-${t.id}`} className={`bg-[#0f1019] border rounded-2xl overflow-hidden ${t.priority === "urgent" && t.status !== "resolved" ? "border-red-500/25" : "border-white/[0.07]"}`}>
                      <div className="p-4 flex items-center justify-between gap-3 cursor-pointer" onClick={() => setExpandedTicket(expandedTicket === t.id ? null : t.id)}>
                        <div className="flex min-w-0 items-center gap-3">
                          {t.status === 'resolved' ? <CheckCircle className="w-4 h-4 shrink-0 text-green-400" /> : t.status === 'in_progress' ? <Clock className="w-4 h-4 shrink-0 text-yellow-400" /> : <AlertCircle className="w-4 h-4 shrink-0 text-blue-400" />}
                          <div className="min-w-0">
                            <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                              <span className="truncate">{t.subject}</span>
                              {t.product_name && <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300">{t.product_name}</span>}
                              {t.source === "ai" && <span className="inline-flex items-center gap-1 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-violet-200"><Brain className="h-3 w-3" />IA</span>}
                            </p>
                            <p className="text-xs text-white/35">{t.username || t.user_email} — {new Date(t.created_at).toLocaleDateString('fr-FR')}{t.category_label ? ` · ${t.category_label}` : ""}</p>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <span data-testid={`admin-ticket-priority-${t.id}`} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${prio.cls}`}>{prio.label}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${t.status === 'resolved' ? 'bg-green-500/10 text-green-400' : t.status === 'in_progress' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-blue-500/10 text-blue-400'}`}>
                            {t.status === 'resolved' ? 'Résolu' : t.status === 'in_progress' ? 'En cours' : 'Ouvert'}
                          </span>
                        </div>
                      </div>
                      {expandedTicket === t.id && (
                        <div className="border-t border-white/5 p-4 space-y-3">
                          {t.ai_summary && (
                            <div className="rounded-xl border border-violet-500/20 bg-violet-500/[0.07] p-3">
                              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-violet-200"><Brain className="h-3.5 w-3.5" />Résumé de l'assistant</p>
                              <p className="text-sm text-white/80 whitespace-pre-wrap">{t.ai_summary}</p>
                            </div>
                          )}
                          <div className="bg-black/20 rounded-xl p-3"><p className="text-xs text-white/30 mb-1">{t.username}</p><p className="text-sm text-white/70 whitespace-pre-wrap">{t.message}</p></div>
                          {(t.replies || []).map((r, i) => (
                            <div key={`${t.id}-reply-${i}`} className={`rounded-xl p-3 ${r.from === 'admin' ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-black/20'}`}>
                              <p className="text-xs mb-1" style={{ color: r.from === 'admin' ? '#5865F2' : 'rgba(255,255,255,0.3)' }}>{r.from === 'admin' ? 'Admin' : t.username}</p>
                              <p className="text-sm text-white/70">{r.message}</p>
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Répondre..." className="bg-black/30 border-white/10 rounded-lg flex-1 text-sm" onKeyDown={e => e.key === 'Enter' && replyTicket(t.id)} />
                            <Button onClick={() => replyTicket(t.id)} className="bg-blue-600 rounded-lg"><Send className="w-4 h-4" /></Button>
                          </div>
                          <div className="flex gap-2">
                            {t.status !== 'resolved' && <Button size="sm" onClick={() => updateTicketStatus(t.id, 'resolved')} className="bg-green-600/20 text-green-400 rounded-lg text-xs"><CheckCircle className="w-3 h-3 mr-1" />Résolu</Button>}
                            {t.status !== 'in_progress' && t.status !== 'resolved' && <Button size="sm" onClick={() => updateTicketStatus(t.id, 'in_progress')} className="bg-yellow-600/20 text-yellow-400 rounded-lg text-xs"><Clock className="w-3 h-3 mr-1" />En cours</Button>}
                            {t.status === 'resolved' && <Button size="sm" onClick={() => updateTicketStatus(t.id, 'open')} className="bg-blue-600/20 text-blue-400 rounded-lg text-xs">Réouvrir</Button>}
                            <Button size="sm" onClick={() => deleteTicket(t.id)} className="bg-red-600/20 text-red-400 rounded-lg text-xs"><Trash2 className="w-3 h-3 mr-1" />Supprimer</Button>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })()}


          {/* REVIEWS */}
          {tab === "reviews" && (
            <div>
              <h2 className="text-lg font-semibold mb-1">Avis clients</h2>
              <p className="text-white/40 text-sm mb-6">{reviews.length} avis — Moyenne: {reviews.length > 0 ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : 0}/5</p>
              {reviews.length === 0 ? (
                <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-12 text-center"><Star className="w-10 h-10 text-white/10 mx-auto mb-3" /><p className="text-white/30">Aucun avis</p></div>
              ) : (
                <div className="space-y-3">
                  {reviews.map(r => (
                    <div key={r.id} className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex">{[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${r.rating >= n ? 'text-yellow-400 fill-yellow-400' : 'text-white/10'}`} />)}</div>
                        <div>
                          <p className="text-sm font-medium">{r.username}{r.product_name && <span className="ml-2 text-xs text-blue-300">{r.product_name}</span>}{r.verified && <span className="ml-2 text-[10px] text-green-400">achat vérifié</span>}</p>
                          <p className="text-xs text-white/30 max-w-md truncate">{r.comment}</p>
                          {r.images?.length > 0 && <div className="flex gap-1 mt-1">{r.images.map((src) => <a key={src} href={src} target="_blank" rel="noreferrer"><img src={src} alt="" className="w-10 h-10 object-cover rounded" /></a>)}</div>}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => deleteReview(r.id)} className="text-red-400 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SITE STATUS */}
          {tab === "site-status" && (
            <div>
              <h2 className="text-lg font-semibold mb-1">Statut & maintenance</h2>
              <p className="text-white/40 text-sm mb-6">Contrôlez le statut public affiché sur <code className="text-blue-400">/status</code>, et le mode maintenance du site.</p>

              <div className={`bg-[#12121a] border rounded-lg p-6 mb-6 ${siteSettings.maintenance_mode ? 'border-red-500/30' : 'border-white/5'}`}>
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div>
                    <h2 className="font-semibold flex items-center gap-2">
                      <AlertTriangle className={`w-4 h-4 ${siteSettings.maintenance_mode ? 'text-red-400' : 'text-white/30'}`} />
                      Mode maintenance
                    </h2>
                    <p className="text-white/40 text-xs mt-1">Bloque l'accès au site pour tous les visiteurs, sauf les admins.</p>
                  </div>
                  <button
                    data-testid="maintenance-toggle"
                    onClick={() => setSiteSettings({ ...siteSettings, maintenance_mode: !siteSettings.maintenance_mode })}
                    className={`w-12 h-6 rounded-full transition-colors relative shrink-0 ${siteSettings.maintenance_mode ? 'bg-red-500' : 'bg-white/10'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${siteSettings.maintenance_mode ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                <Label className="text-white/60 text-xs">Message affiché aux visiteurs (optionnel)</Label>
                <textarea
                  data-testid="maintenance-message-input"
                  value={siteSettings.maintenance_message || ""}
                  onChange={(e) => setSiteSettings({ ...siteSettings, maintenance_message: e.target.value })}
                  rows={3}
                  placeholder="Ex : Maintenance en cours, retour prévu à 18h."
                  className="w-full bg-black/30 border border-white/10 rounded-lg mt-1 p-3 text-sm text-white resize-none focus:outline-none focus:border-red-500/40"
                />
                <Button data-testid="save-maintenance-btn" onClick={saveSiteSettings} className="mt-4 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm">Enregistrer</Button>
              </div>

              <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-6">
                <h2 className="font-semibold mb-1">Statut de la boutique</h2>
                <p className="text-white/40 text-xs mb-4">Le statut de chaque produit vendu dans la boutique (rubrique Boutique → Produits). En <b className="text-amber-300">Maintenance</b> ou <b className="text-red-300">Panne</b>, personne ne peut l'acheter, le renouveler ni l'essayer : le bouton d'achat est grisé avec « achat indisponible ».</p>
                <div className="space-y-3">
                  {shopBots.map((bot) => (
                    <div key={bot.id} className="flex items-center justify-between bg-black/20 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: bot.color || '#5865F2' }} />
                        <span className="font-medium">{bot.name}</span>
                      </div>
                      <Select value={bot.status || "operational"} onValueChange={(v) => updateShopBotStatus(bot.id, v)}>
                        <SelectTrigger data-testid={`bot-status-${bot.id}`} className="bg-black/30 border-white/10 rounded-lg w-48 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#12121a] border-white/10">
                          <SelectItem value="operational">Opérationnel</SelectItem>
                          <SelectItem value="maintenance">Maintenance (achat bloqué)</SelectItem>
                          <SelectItem value="outage">Panne (achat bloqué)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  {shopBots.length === 0 && <p className="text-white/30 text-sm">Aucun produit dans la boutique</p>}
                </div>
              </div>
            </div>
          )}

          {/* ÉVÉNEMENTS & FÊTES */}
          {tab === "season" && <SeasonPanel hdr={hdr} />}
          {tab === "ambassadors" && <AmbassadorsPanel hdr={hdr} />}
          {tab === "netbans" && <NetbanPanel hdr={hdr} />}
          {tab === "all-bots" && <AllBotsPanel hdr={stableHdr} />}
          {tab === "scripts" && <FounderScripts hdr={stableHdr} />}
          {tab === "vanta-ai" && <VantaAIPanel hdr={stableHdr} onOpenChats={(u) => { setVaiChatUser(u); setTab("vai-chats"); }} onOpenModeration={() => setTab("vai-moderation")} />}
          {tab === "vai-clients" && <VantaAIClients hdr={stableHdr} />}
          {tab === "vai-chats" && <VantaAIChats hdr={stableHdr} user={vaiChatUser} onClearUser={() => setVaiChatUser(null)} />}
          {tab === "vai-training" && <VantaAITraining hdr={stableHdr} />}
          {tab === "vai-moderation" && <VantaAIModeration hdr={stableHdr} />}

          {/* API KEYS */}
          {tab === "api-keys" && (
            <div>
              <h2 className="text-lg font-semibold mb-1" data-testid="api-keys-title">Clé API Universelle</h2>
              <p className="text-white/40 text-sm mb-6">Une seule clé pour tous vos bots. Votre script Discord utilise cette clé dans le header <code className="text-blue-400">x-api-key</code> pour appeler les endpoints <code className="text-blue-400">/api/external-bots/*</code></p>
              <div className="bg-[#12121a] border border-cyan-500/20 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Key className="w-5 h-5 text-cyan-400" />
                  <span className="font-semibold text-cyan-300">Clé API Universelle</span>
                </div>
                {apiKeys.api_key ? (
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input readOnly value={apiKeys.api_key} data-testid="api-key-input" className="bg-black/30 border-white/10 rounded-lg font-mono text-sm flex-1 text-cyan-300" />
                      <Button data-testid="copy-api-key-btn" onClick={() => { navigator.clipboard.writeText(apiKeys.api_key); toast.success("Clé copiée !"); }} variant="outline" className="border-white/10 rounded-lg text-sm">Copier</Button>
                    </div>
                    <Button data-testid="regenerate-api-key-btn" onClick={genApiKey} variant="ghost" className="text-red-400 text-xs h-7"><RefreshCw className="w-3 h-3 mr-1" />Régénérer la clé</Button>
                  </div>
                ) : (
                  <Button data-testid="generate-api-key-btn" onClick={genApiKey} className="rounded-lg text-sm bg-cyan-600 hover:bg-cyan-500"><Key className="w-4 h-4 mr-1.5" />Générer une clé</Button>
                )}
                <div className="mt-6 p-4 bg-black/40 rounded-lg border border-white/5">
                  <p className="text-white/50 text-xs font-semibold mb-2">ENDPOINTS DISPONIBLES :</p>
                  <div className="space-y-1 text-xs font-mono">
                    <p className="text-green-400">GET /api/external-bots/config <span className="text-white/30">→ tous les bots actifs + en période de grâce</span></p>
                    <p className="text-green-400">GET /api/external-bots/notifications <span className="text-white/30">→ événements (achats, renouvellements...)</span></p>
                    <p className="text-green-400">POST /api/external-bots/verify <span className="text-white/30">→ vérifier un utilisateur (active / grace_period / expired)</span></p>
                    <p className="text-green-400">POST /api/external-bots/renew <span className="text-white/30">→ générer un lien de renouvellement</span></p>
                    <p className="text-green-400">POST /api/external-bots/cleanup <span className="text-white/30">→ supprimer les bots hors période de grâce</span></p>
                    <p className="text-yellow-400 mt-2">GET /api/gestion/notifications <span className="text-white/30">→ filtré par type "gestion"</span></p>
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* AI CONFIGURATION */}
          {tab === "ai-config" && (
            <div>
              <h2 className="text-lg font-semibold mb-1" data-testid="ai-config-title">Chatbot IA du support</h2>
              <p className="text-white/40 text-sm mb-6">Ces informations sont utilisées uniquement par le Support VantaPulse AI public. Ajoutez les règles, tarifs, horaires, procédures et réponses que vous voulez lui faire respecter.</p>
              
              <div className="bg-[#12121a] border border-cyan-500/20 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Brain className="w-5 h-5 text-cyan-400" />
                  <div><span className="font-semibold text-cyan-300">Base de connaissances du Support VantaPulse AI</span><p className="text-xs text-white/35 mt-1">L’IA s’appuie sur ce texte pour personnaliser ses réponses.</p></div>
                </div>
                <textarea
                  data-testid="ai-knowledge-input"
                  value={aiKnowledge}
                  onChange={(e) => setAiKnowledge(e.target.value)}
                  placeholder="Écrivez ici tout ce que le Support VantaPulse AI doit savoir...&#10;&#10;Exemples :&#10;- Pour réinitialiser un bot, ouvrir un ticket avec son identifiant&#10;- Les paiements se font via Stripe&#10;- Le délai de réponse humain est de 24 heures&#10;- Pour contacter un humain : support@vantapulse.fr&#10;- Ne jamais promettre une fonctionnalité non disponible"
                  rows={15}
                  className="w-full bg-black/30 border border-white/10 rounded-lg p-4 text-sm text-white/80 placeholder:text-white/20 outline-none focus:border-cyan-500/30 resize-y"
                />
                <div className="flex justify-between items-center mt-4">
                  <p className="text-white/30 text-xs">{aiKnowledge.length} caractères</p>
                  <Button
                    data-testid="save-ai-knowledge-btn"
                    onClick={async () => {
                      setAiSaving(true);
                      try {
                        await axios.put(`${API}/support-ai/admin/knowledge`, { content: aiKnowledge }, { headers: hdr() });
                        toast.success("Connaissances IA mises à jour !");
                      } catch { toast.error("Erreur"); }
                      finally { setAiSaving(false); }
                    }}
                    disabled={aiSaving}
                    className="bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm"
                  >
                    {aiSaving ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>

      {/* === MODALS === */}

      {/* ADD SHOP BOT */}
      <Dialog open={showAddShopBot} onOpenChange={setShowAddShopBot}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Store className="w-4 h-4 text-blue-400" />Ajouter un produit à la boutique</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-white/60 text-xs">ID unique (ex: music)</Label><Input value={newShopBot.id} onChange={e => setNewShopBot({ ...newShopBot, id: e.target.value.toLowerCase().replace(/\s/g, '-') })} className="bg-black/30 border-white/10 rounded-lg mt-1 font-mono" /></div>
            <div><Label className="text-white/60 text-xs">Nom</Label><Input value={newShopBot.name} onChange={e => setNewShopBot({ ...newShopBot, name: e.target.value })} placeholder="Vanta Pulse Music" className="bg-black/30 border-white/10 rounded-lg mt-1" /></div>
            <div><Label className="text-white/60 text-xs">Couleur</Label><Input type="color" value={newShopBot.color} onChange={e => setNewShopBot({ ...newShopBot, color: e.target.value })} className="bg-black/30 border-white/10 rounded-lg mt-1 h-10 p-1" /></div>
            <div><Label className="text-white/60 text-xs">Description du produit</Label><textarea value={newShopBot.description} onChange={e => setNewShopBot({ ...newShopBot, description: e.target.value })} rows={3} placeholder="Expliquez ce que fait ce produit..." className="w-full bg-black/30 border border-white/10 rounded-lg mt-1 p-3 text-sm text-white resize-none focus:outline-none focus:border-blue-500" /></div>
            <div><Label className="text-white/60 text-xs">3 images de démonstration</Label>{newShopBot.demo_images.map((url, index) => <label key={`new-demo-${index}`} className="flex items-center gap-3 mt-1 rounded-lg border border-white/10 bg-black/30 p-2 cursor-pointer hover:border-blue-500/50"><input type="file" accept="image/*" className="hidden" onChange={async e => { const file = e.target.files[0]; if (!file) return; const reader = new FileReader(); reader.onload = async () => { try { const r = await axios.post(`${API}/upload/image`, { image: reader.result }, { headers: hdr() }); setNewShopBot(current => ({ ...current, demo_images: current.demo_images.map((image, imageIndex) => imageIndex === index ? r.data.url : image) })); toast.success(`Image ${index + 1} uploadée`); } catch { toast.error("Erreur upload"); } }; reader.readAsDataURL(file); }} /><span className="flex-1 text-xs text-white/40">{url ? `Image ${index + 1} sélectionnée` : `Choisir l'image ${index + 1}`}</span>{url && <img src={url} alt="" className="h-10 w-16 rounded object-cover" />}</label>)}</div>
            <div><Label className="text-white/60 text-xs">Logo</Label>
              <label className="flex items-center justify-center w-full h-20 mt-1 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-blue-500/50 transition-colors bg-black/20">
                {newShopBot.image_url ? (
                  <div className="relative"><img src={newShopBot.image_url} alt="" className="h-16 rounded object-cover" /><button onClick={(e) => { e.preventDefault(); setNewShopBot({ ...newShopBot, image_url: "" }); }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">x</button></div>
                ) : (
                  <div className="text-center"><Upload className="w-4 h-4 text-white/20 mx-auto mb-1" /><p className="text-[10px] text-white/30">Logo (carré)</p></div>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  const reader = new FileReader();
                  reader.onload = async () => { try { const r = await axios.post(`${API}/upload/image`, { image: reader.result }, { headers: hdr() }); setNewShopBot({ ...newShopBot, image_url: r.data.url }); toast.success("Logo uploadé"); } catch { toast.error("Erreur"); } };
                  reader.readAsDataURL(file);
                }} />
              </label>
            </div>
            <div><Label className="text-white/60 text-xs">Bannière</Label>
              <label className="flex items-center justify-center w-full h-20 mt-1 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-blue-500/50 transition-colors bg-black/20">
                {newShopBot.banner_url ? (
                  <div className="relative"><img src={newShopBot.banner_url} alt="" className="h-16 rounded object-cover" /><button onClick={(e) => { e.preventDefault(); setNewShopBot({ ...newShopBot, banner_url: "" }); }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">x</button></div>
                ) : (
                  <div className="text-center"><Upload className="w-4 h-4 text-white/20 mx-auto mb-1" /><p className="text-[10px] text-white/30">Bannière (large)</p></div>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  const reader = new FileReader();
                  reader.onload = async () => { try { const r = await axios.post(`${API}/upload/image`, { image: reader.result }, { headers: hdr() }); setNewShopBot({ ...newShopBot, banner_url: r.data.url }); toast.success("Bannière uploadée"); } catch { toast.error("Erreur"); } };
                  reader.readAsDataURL(file);
                }} />
              </label>
            </div>

            <div className="border-t border-white/5 pt-3">
              <div className="flex items-center gap-2 mb-1">
                <Label className="text-white/60 text-xs">Produit téléchargeable</Label>
                <button onClick={() => setNewShopBot({ ...newShopBot, downloadable: !newShopBot.downloadable })} className={`w-10 h-5 rounded-full transition-colors relative ${newShopBot.downloadable ? 'bg-green-500' : 'bg-white/10'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${newShopBot.downloadable ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <p className="text-white/30 text-[11px] mb-2">L'acheteur reçoit un bouton Télécharger et une clé de licence (utilisable sur un seul appareil) dans son tableau de bord.</p>
              {newShopBot.downloadable && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button onClick={() => setNewShopBot({ ...newShopBot, download_mode: "link" })} className={`px-3 py-1.5 rounded text-xs transition-colors ${(newShopBot.download_mode || "link") === "link" ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/40'}`}>Lien</button>
                    <button onClick={() => setNewShopBot({ ...newShopBot, download_mode: "file" })} className={`px-3 py-1.5 rounded text-xs transition-colors ${newShopBot.download_mode === "file" ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/40'}`}>Fichier</button>
                  </div>
                  {(newShopBot.download_mode || "link") === "link" ? (
                    <Input value={newShopBot.download_url} onChange={e => setNewShopBot({ ...newShopBot, download_url: e.target.value, download_filename: "" })} placeholder="Lien de téléchargement (https://...)" className="bg-black/30 border-white/10 rounded-lg text-sm" />
                  ) : (
                    <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-2 cursor-pointer hover:border-blue-500/50">
                      <input type="file" className="hidden" onChange={async e => {
                        const file = e.target.files[0]; if (!file) return;
                        if (file.size > 20 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 20 Mo) — utilisez un lien pour un fichier plus gros"); return; }
                        const reader = new FileReader();
                        reader.onload = async () => {
                          try {
                            const r = await axios.post(`${API}/admin/upload/file`, { data: reader.result, filename: file.name, content_type: file.type || "application/octet-stream" }, { headers: hdr() });
                            setNewShopBot(current => ({ ...current, download_url: r.data.url, download_filename: r.data.filename }));
                            toast.success("Fichier hébergé");
                          } catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
                        };
                        reader.readAsDataURL(file);
                      }} />
                      <Upload className="w-4 h-4 text-white/30 shrink-0" />
                      <span className="flex-1 text-xs text-white/40 truncate">{newShopBot.download_filename || "Choisir un fichier (max 20 Mo)"}</span>
                    </label>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-white/5 pt-3">
              <Label className="text-white/60 text-xs mb-2 block">Plans / Tarifs</Label>
              {newShopBot.plans.map((p, i) => (
                <div key={`new-plan-${p.name}-${i}`} className="flex items-center gap-2 mb-2 text-sm bg-white/5 rounded p-2">
                  <span className="flex-1">{p.name}</span>
                  <label className="flex items-center gap-1 text-blue-400" title="Modifier le prix">
                      <input type="number" step="0.01" min="0" inputMode="decimal" value={p.price}
                        onChange={e => setNewShopBot({ ...newShopBot, plans: withPlanPrice(newShopBot.plans, i, e.target.value) })}
                        aria-label={`Prix du plan ${p.name}`} data-testid={`new-plan-price-${i}`}
                        className="w-20 bg-black/30 border border-white/10 hover:border-white/20 focus:border-blue-500/60 rounded px-1.5 py-0.5 text-right font-medium text-blue-400 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />€
                    </label>
                  <span className="text-white/40">{p.duration_label}</span>
                  <button onClick={() => setNewShopBot({ ...newShopBot, plans: newShopBot.plans.filter((_, idx) => idx !== i) })} className="text-red-400"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              <div className="bg-white/[0.02] rounded-lg p-3 space-y-2 border border-white/5">
                <div className="grid grid-cols-2 gap-2">
                  <Input value={newPlan.name} onChange={e => setNewPlan({ ...newPlan, name: e.target.value })} placeholder="Nom du plan" className="bg-black/30 border-white/10 rounded-lg text-sm" />
                  <Input type="number" step="0.5" min="0.5" value={newPlan.price} onChange={e => setNewPlan({ ...newPlan, price: parseFloat(e.target.value) || 0 })} placeholder="Prix €" className="bg-black/30 border-white/10 rounded-lg text-sm" />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setNewPlan({ ...newPlan, is_lifetime: !newPlan.is_lifetime })} className={`px-3 py-1.5 rounded text-xs transition-colors ${newPlan.is_lifetime ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/40'}`}>A vie</button>
                  {!newPlan.is_lifetime && (
                    <>
                      <button onClick={() => setNewPlan({ ...newPlan, duration_unit: "months" })} className={`px-3 py-1.5 rounded text-xs transition-colors ${newPlan.duration_unit === 'months' ? 'bg-white/10 text-white' : 'bg-white/5 text-white/40'}`}>Mois</button>
                      <button onClick={() => setNewPlan({ ...newPlan, duration_unit: "days" })} className={`px-3 py-1.5 rounded text-xs transition-colors ${newPlan.duration_unit === 'days' ? 'bg-white/10 text-white' : 'bg-white/5 text-white/40'}`}>Jours</button>
                    </>
                  )}
                </div>
                {!newPlan.is_lifetime && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" min="1" value={newPlan.duration_days} onChange={e => setNewPlan({ ...newPlan, duration_days: parseInt(e.target.value) || 1 })} placeholder={newPlan.duration_unit === 'months' ? 'Nb mois' : 'Nb jours'} className="bg-black/30 border-white/10 rounded-lg text-sm" />
                    <Input value={newPlan.duration_label} onChange={e => setNewPlan({ ...newPlan, duration_label: e.target.value })} placeholder="1 mois, 90 jours..." className="bg-black/30 border-white/10 rounded-lg text-sm" />
                  </div>
                )}
                <Button onClick={addPlanToNew} variant="outline" className="w-full border-white/10 rounded-lg text-sm"><Plus className="w-3 h-3 mr-1" />Ajouter ce plan</Button>
              </div>
            </div>
            <ProductExtrasFields product={newShopBot} setProduct={setNewShopBot} shopBots={shopBots} />
            <Button onClick={createShopBot} className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">Créer le produit</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT SHOP BOT */}
      <Dialog open={!!showEditShopBot} onOpenChange={() => setShowEditShopBot(null)}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier {showEditShopBot?.name}</DialogTitle></DialogHeader>
          {showEditShopBot && (
            <div className="space-y-3">
              <div><Label className="text-white/60 text-xs">Nom</Label><Input value={showEditShopBot.name} onChange={e => setShowEditShopBot({ ...showEditShopBot, name: e.target.value })} className="bg-black/30 border-white/10 rounded-lg mt-1" /></div>
              <div><Label className="text-white/60 text-xs">Couleur</Label><Input type="color" value={showEditShopBot.color} onChange={e => setShowEditShopBot({ ...showEditShopBot, color: e.target.value })} className="bg-black/30 border-white/10 rounded-lg mt-1 h-10 p-1" /></div>
              <div><Label className="text-white/60 text-xs">Description du produit</Label><textarea value={showEditShopBot.description || ""} onChange={e => setShowEditShopBot({ ...showEditShopBot, description: e.target.value })} rows={3} className="w-full bg-black/30 border border-white/10 rounded-lg mt-1 p-3 text-sm text-white resize-none focus:outline-none focus:border-blue-500" /></div>
              <div><Label className="text-white/60 text-xs">Images de démonstration</Label>{demoSlots(showEditShopBot.demo_images).map(index => { const image = showEditShopBot.demo_images?.[index]; const url = typeof image === "string" ? image : image?.url || ""; return <label key={`edit-demo-${index}`} className="flex items-center gap-3 mt-1 rounded-lg border border-white/10 bg-black/30 p-2 cursor-pointer hover:border-blue-500/50"><input type="file" accept="image/*" className="hidden" onChange={async e => { const file = e.target.files[0]; if (!file) return; const reader = new FileReader(); reader.onload = async () => { try { const r = await axios.post(`${API}/upload/image`, { image: reader.result }, { headers: hdr() }); setShowEditShopBot(current => ({ ...current, demo_images: demoSlots(current.demo_images).map(imageIndex => imageIndex === index ? { url: r.data.url } : (current.demo_images?.[imageIndex] || { url: "" })) })); toast.success(`Image ${index + 1} uploadée`); } catch { toast.error("Erreur upload"); } }; reader.readAsDataURL(file); }} /><span className="flex-1 text-xs text-white/40">{url ? `Image ${index + 1} sélectionnée` : `Choisir l'image ${index + 1}`}</span>{url && <img src={url} alt="" className="h-10 w-16 rounded object-cover" />}</label>; })}</div>
              <div><Label className="text-white/60 text-xs">Logo</Label>
                <label className="flex items-center justify-center w-full h-20 mt-1 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-blue-500/50 transition-colors bg-black/20">
                  {showEditShopBot.image_url ? (
                    <div className="relative"><img src={showEditShopBot.image_url} alt="" className="h-16 rounded object-cover" /><button onClick={(e) => { e.preventDefault(); setShowEditShopBot({ ...showEditShopBot, image_url: "" }); }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">x</button></div>
                  ) : (
                    <div className="text-center"><Upload className="w-4 h-4 text-white/20 mx-auto mb-1" /><p className="text-[10px] text-white/30">Logo (carré)</p></div>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    const reader = new FileReader();
                    reader.onload = async () => { try { const r = await axios.post(`${API}/upload/image`, { image: reader.result }, { headers: hdr() }); setShowEditShopBot({ ...showEditShopBot, image_url: r.data.url }); toast.success("Logo uploadé"); } catch { toast.error("Erreur"); } };
                    reader.readAsDataURL(file);
                  }} />
                </label>
              </div>
              <div><Label className="text-white/60 text-xs">Bannière</Label>
                <label className="flex items-center justify-center w-full h-20 mt-1 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-blue-500/50 transition-colors bg-black/20">
                  {showEditShopBot.banner_url ? (
                    <div className="relative"><img src={showEditShopBot.banner_url} alt="" className="h-16 rounded object-cover" /><button onClick={(e) => { e.preventDefault(); setShowEditShopBot({ ...showEditShopBot, banner_url: "" }); }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">x</button></div>
                  ) : (
                    <div className="text-center"><Upload className="w-4 h-4 text-white/20 mx-auto mb-1" /><p className="text-[10px] text-white/30">Bannière (large)</p></div>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    const reader = new FileReader();
                    reader.onload = async () => { try { const r = await axios.post(`${API}/upload/image`, { image: reader.result }, { headers: hdr() }); setShowEditShopBot({ ...showEditShopBot, banner_url: r.data.url }); toast.success("Bannière uploadée"); } catch { toast.error("Erreur"); } };
                    reader.readAsDataURL(file);
                  }} />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-white/60 text-xs">Actif</Label>
                <button onClick={() => setShowEditShopBot({ ...showEditShopBot, active: !showEditShopBot.active })} className={`w-10 h-5 rounded-full transition-colors relative ${showEditShopBot.active ? 'bg-green-500' : 'bg-white/10'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${showEditShopBot.active ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="border-t border-white/5 pt-3">
                <div className="flex items-center gap-2 mb-1">
                  <Label className="text-white/60 text-xs">Produit téléchargeable</Label>
                  <button onClick={() => setShowEditShopBot({ ...showEditShopBot, downloadable: !showEditShopBot.downloadable })} className={`w-10 h-5 rounded-full transition-colors relative ${showEditShopBot.downloadable ? 'bg-green-500' : 'bg-white/10'}`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${showEditShopBot.downloadable ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                <p className="text-white/30 text-[11px] mb-2">L'acheteur reçoit un bouton Télécharger et une clé de licence (utilisable sur un seul appareil) dans son tableau de bord.</p>
                {showEditShopBot.downloadable && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button onClick={() => setShowEditShopBot({ ...showEditShopBot, download_mode: "link" })} className={`px-3 py-1.5 rounded text-xs transition-colors ${(showEditShopBot.download_mode || "link") === "link" ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/40'}`}>Lien</button>
                      <button onClick={() => setShowEditShopBot({ ...showEditShopBot, download_mode: "file" })} className={`px-3 py-1.5 rounded text-xs transition-colors ${showEditShopBot.download_mode === "file" ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/40'}`}>Fichier</button>
                    </div>
                    {(showEditShopBot.download_mode || "link") === "link" ? (
                      <Input value={showEditShopBot.download_url || ""} onChange={e => setShowEditShopBot({ ...showEditShopBot, download_url: e.target.value, download_filename: "" })} placeholder="Lien de téléchargement (https://...)" className="bg-black/30 border-white/10 rounded-lg text-sm" />
                    ) : (
                      <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-2 cursor-pointer hover:border-blue-500/50">
                        <input type="file" className="hidden" onChange={async e => {
                          const file = e.target.files[0]; if (!file) return;
                          if (file.size > 20 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 20 Mo) — utilisez un lien pour un fichier plus gros"); return; }
                          const reader = new FileReader();
                          reader.onload = async () => {
                            try {
                              const r = await axios.post(`${API}/admin/upload/file`, { data: reader.result, filename: file.name, content_type: file.type || "application/octet-stream" }, { headers: hdr() });
                              setShowEditShopBot(current => ({ ...current, download_url: r.data.url, download_filename: r.data.filename }));
                              toast.success("Fichier hébergé");
                            } catch (err) { toast.error(err.response?.data?.detail || "Erreur upload"); }
                          };
                          reader.readAsDataURL(file);
                        }} />
                        <Upload className="w-4 h-4 text-white/30 shrink-0" />
                        <span className="flex-1 text-xs text-white/40 truncate">{showEditShopBot.download_filename || "Choisir un fichier (max 20 Mo)"}</span>
                      </label>
                    )}
                  </div>
                )}
              </div>
              <ProductExtrasFields product={showEditShopBot} setProduct={setShowEditShopBot} shopBots={shopBots} />
              <div className="border-t border-white/5 pt-3">
                <Label className="text-white/60 text-xs mb-2 block">Plans</Label>
                {(showEditShopBot.plans || []).map((p, i) => (
                  <div key={`edit-plan-${p.name}-${i}`} className="flex items-center gap-2 mb-2 text-sm bg-white/5 rounded p-2">
                    <span className="flex-1">{p.name}</span>
                    <label className="flex items-center gap-1 text-blue-400" title="Modifier le prix">
                      <input type="number" step="0.01" min="0" inputMode="decimal" value={p.price}
                        onChange={e => setShowEditShopBot({ ...showEditShopBot, plans: withPlanPrice(showEditShopBot.plans, i, e.target.value) })}
                        aria-label={`Prix du plan ${p.name}`} data-testid={`edit-plan-price-${i}`}
                        className="w-20 bg-black/30 border border-white/10 hover:border-white/20 focus:border-blue-500/60 rounded px-1.5 py-0.5 text-right font-medium text-blue-400 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />€
                    </label>
                    <span className="text-white/40">{p.duration_label}</span>
                    <button onClick={() => setShowEditShopBot({ ...showEditShopBot, plans: showEditShopBot.plans.filter((_, idx) => idx !== i) })} className="text-red-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                <div className="bg-white/[0.02] rounded-lg p-3 space-y-2 border border-white/5">
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={newPlan.name} onChange={e => setNewPlan({ ...newPlan, name: e.target.value })} placeholder="Nom" className="bg-black/30 border-white/10 rounded-lg text-sm" />
                    <Input type="number" step="0.5" min="0.5" value={newPlan.price} onChange={e => setNewPlan({ ...newPlan, price: parseFloat(e.target.value) || 0 })} placeholder="Prix €" className="bg-black/30 border-white/10 rounded-lg text-sm" />
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setNewPlan({ ...newPlan, is_lifetime: !newPlan.is_lifetime })} className={`px-3 py-1.5 rounded text-xs transition-colors ${newPlan.is_lifetime ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/40'}`}>A vie</button>
                    {!newPlan.is_lifetime && (
                      <>
                        <button onClick={() => setNewPlan({ ...newPlan, duration_unit: "months" })} className={`px-3 py-1.5 rounded text-xs transition-colors ${newPlan.duration_unit === 'months' ? 'bg-white/10 text-white' : 'bg-white/5 text-white/40'}`}>Mois</button>
                        <button onClick={() => setNewPlan({ ...newPlan, duration_unit: "days" })} className={`px-3 py-1.5 rounded text-xs transition-colors ${newPlan.duration_unit === 'days' ? 'bg-white/10 text-white' : 'bg-white/5 text-white/40'}`}>Jours</button>
                      </>
                    )}
                  </div>
                  {!newPlan.is_lifetime && (
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" min="1" value={newPlan.duration_days} onChange={e => setNewPlan({ ...newPlan, duration_days: parseInt(e.target.value) || 1 })} placeholder={newPlan.duration_unit === 'months' ? 'Nb mois' : 'Nb jours'} className="bg-black/30 border-white/10 rounded-lg text-sm" />
                      <Input value={newPlan.duration_label} onChange={e => setNewPlan({ ...newPlan, duration_label: e.target.value })} placeholder="1 mois, 90 jours..." className="bg-black/30 border-white/10 rounded-lg text-sm" />
                    </div>
                  )}
                  <Button onClick={addPlanToEdit} variant="outline" className="w-full border-white/10 rounded-lg text-sm"><Plus className="w-3 h-3 mr-1" />Ajouter</Button>
                </div>
              </div>
              <Button data-testid="save-shop-edit" onClick={updateShopBot} className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">Sauvegarder</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ADD FREE BOT */}
      <Dialog open={showAddFreeBot} onOpenChange={setShowAddFreeBot}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Gift className="w-4 h-4 text-green-400" />Offrir un produit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {freeBotForm.botType.startsWith("vai:") ? (
              <p className="text-xs text-white/50">
                <span className="text-white font-medium">{freeBotForm.userLabel || "Cet utilisateur"}</span> a Vanta AI {freeBotForm.botType === "vai:max" ? "Max" : "Pro"} tout de suite sur /ai, sans rien accepter. Tu le retrouves dans Clients → Vanta AI pour le prolonger ou l'arrêter.
              </p>
            ) : (
              <p className="text-xs text-white/50">
                Une notification sera envoyée à <span className="text-white font-medium">{freeBotForm.userLabel || "cet utilisateur"}</span>. Il devra accepter l'offre pour l'activer (pour un bot Discord : choisir son nom et fournir son token ; pour un script : il reçoit directement sa clé de licence).
              </p>
            )}
            <div><Label className="text-white/60 text-xs">Type</Label>
              <Select value={freeBotForm.botType} onValueChange={v => setFreeBotForm({ ...freeBotForm, botType: v })}>
                <SelectTrigger className="bg-black/30 border-white/10 rounded-lg mt-1"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent className="bg-[#12121a] border-white/10">
                  {shopBots.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  {shopBots.length === 0 && <><SelectItem value="gestion">Gestion</SelectItem><SelectItem value="radio">Radio</SelectItem></>}
                  {isFullAdmin && <><SelectItem value="vai:pro">Vanta AI Pro</SelectItem><SelectItem value="vai:max">Vanta AI Max</SelectItem></>}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-white/60 text-xs">Durée</Label><Select value={String(freeBotForm.duration)} onValueChange={value => setFreeBotForm({ ...freeBotForm, duration: value })}><SelectTrigger className="bg-black/30 border-white/10 rounded-lg mt-1"><SelectValue placeholder="Choisir une durée" /></SelectTrigger><SelectContent className="bg-[#12121a] border-white/10"><SelectItem value="1">1 mois</SelectItem><SelectItem value="3">3 mois</SelectItem><SelectItem value="6">6 mois</SelectItem><SelectItem value="12">12 mois</SelectItem><SelectItem value="lifetime">À vie</SelectItem></SelectContent></Select></div>
            <Button onClick={addFreeBot} className="w-full bg-green-600 hover:bg-green-700 rounded-lg">{freeBotForm.botType.startsWith("vai:") ? "Offrir Vanta AI" : "Envoyer l'offre"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* STAFF ROLE */}
      <Dialog open={!!staffDialogUser} onOpenChange={(open) => !open && setStaffDialogUser(null)}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCog className="w-4 h-4 text-cyan-400" />Statut staff</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium">{staffDialogUser?.username}</p>
              <p className="text-xs text-white/30">{staffDialogUser?.email}</p>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Rôle</Label>
              <Select value={staffDialogRole} onValueChange={setStaffDialogRole}>
                <SelectTrigger className="bg-black/30 border-white/10 rounded-lg mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12121a] border-white/10">
                  <SelectItem value="none">Membre (retirer le staff)</SelectItem>
                  <SelectItem value="support">Support — accès limité</SelectItem>
                  <SelectItem value="developer">Développeur — outils techniques</SelectItem>
                  <SelectItem value="admin">Administrateur — accès complet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setUserStaffRole(staffDialogUser, staffDialogRole)} className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT BOT DURATION */}
      <Dialog open={!!showEditBot} onOpenChange={() => setShowEditBot(null)}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-sm">
          <DialogHeader><DialogTitle>Modifier la durée</DialogTitle></DialogHeader>
          {showEditBot && (
            <div className="space-y-3">
              <div className="p-3 bg-black/30 rounded-lg"><p className="font-medium text-sm">{showEditBot.bot_name}</p></div>
              <Select onValueChange={v => { updateDuration(showEditBot.id, parseInt(v)); setShowEditBot(null); }}>
                <SelectTrigger className="bg-black/30 border-white/10 rounded-lg"><SelectValue placeholder="Ajouter..." /></SelectTrigger>
                <SelectContent className="bg-[#12121a] border-white/10"><SelectItem value="1">+1 mois</SelectItem><SelectItem value="3">+3 mois</SelectItem><SelectItem value="6">+6 mois</SelectItem><SelectItem value="12">+12 mois</SelectItem></SelectContent>
              </Select>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RELIER UN ID DISCORD */}
      <Dialog open={!!showLinkDiscord} onOpenChange={() => { setShowLinkDiscord(null); setLinkDiscordId(""); }}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4 text-[#8791f0]" />Relier un ID Discord</DialogTitle></DialogHeader>
          {showLinkDiscord && (
            <div className="space-y-3">
              <div className="p-3 bg-black/30 rounded-lg">
                <p className="font-medium text-sm">{showLinkDiscord.bot_name}</p>
                <p className="text-white/40 text-xs mt-1">{showLinkDiscord.user_email || '—'} · Discord actuel : {showLinkDiscord.discord_id || 'aucun'}</p>
              </div>
              <p className="text-white/50 text-xs">Pour un client qui a acheté avant d'avoir relié son Discord : le bot du magasin le reconnaît comme client (rôle client gardé) et son bot le reconnaît comme buyer. Le token et la durée ne changent pas.</p>
              <div><Label className="text-white/60 text-xs">ID Discord du client</Label><Input value={linkDiscordId} onChange={e => setLinkDiscordId(e.target.value)} placeholder="ex. 123456789012345678" className="bg-black/30 border-white/10 rounded-lg mt-1 font-mono" data-testid="link-discord-input" /></div>
              <Button onClick={linkDiscord} className="w-full bg-[#5865F2] hover:bg-[#4752c4] rounded-lg" data-testid="link-discord-save">Relier</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* TRANSFER */}
      <Dialog open={!!showTransfer} onOpenChange={() => { setShowTransfer(null); setTransferForm({ new_discord_id: "", new_discord_token: "" }); }}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-blue-400" />Transférer</DialogTitle></DialogHeader>
          {showTransfer && (
            <div className="space-y-3">
              <div className="p-3 bg-black/30 rounded-lg"><p className="font-medium text-sm">{showTransfer.bot_name}</p></div>
              <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 rounded-lg"><p className="text-yellow-400 text-xs">L'ancien propriétaire perdra l'accès.</p></div>
              <div><Label className="text-white/60 text-xs">Nouvel ID Discord</Label><Input value={transferForm.new_discord_id} onChange={e => setTransferForm({ ...transferForm, new_discord_id: e.target.value })} className="bg-black/30 border-white/10 rounded-lg mt-1 font-mono" /></div>
              <div><Label className="text-white/60 text-xs">Nouveau Token</Label><Input value={transferForm.new_discord_token} onChange={e => setTransferForm({ ...transferForm, new_discord_token: e.target.value })} className="bg-black/30 border-white/10 rounded-lg mt-1 font-mono" /></div>
              <Button onClick={transferBot} className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">Transférer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* LICENSE */}
      <Dialog open={!!showLicense} onOpenChange={() => setShowLicense(null)}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Key className="w-4 h-4 text-yellow-400" />Licence</DialogTitle></DialogHeader>
          {showLicense && (
            <div className="space-y-3">
              <div className="p-3 bg-black/30 rounded-lg"><p className="font-medium text-sm">{showLicense.bot_name}</p></div>
              <div>
                <Label className="text-white/60 text-xs">Clé de licence</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-xs font-mono text-white bg-black/30 px-2 py-2 rounded-lg truncate">{showLicense.license_key || "—"}</code>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(showLicense.license_key || ""); toast.success("Clé copiée"); }} className="h-8 w-8 p-0 text-white/40"><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm p-3 bg-black/30 rounded-lg">
                <span className="text-white/50">Appareil lié</span>
                <span className={showLicense.license_device_id ? 'text-green-400' : 'text-white/40'}>{showLicense.license_device_id ? 'Activée' : 'Libre (non activée)'}</span>
              </div>
              <div className="flex items-center justify-between text-sm p-3 bg-black/30 rounded-lg">
                <span className="text-white/50">Statut de la clé</span>
                <span className={showLicense.license_active === false ? 'text-red-400' : 'text-green-400'}>{showLicense.license_active === false ? 'Désactivée' : 'Active'}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={toggleLicense} variant="outline" className="border-white/10 rounded-lg text-sm">
                  <Power className="w-3.5 h-3.5 mr-1.5" />{showLicense.license_active === false ? 'Réactiver' : 'Désactiver'}
                </Button>
                <Button onClick={regenerateLicense} className="bg-yellow-600 hover:bg-yellow-700 rounded-lg text-sm">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Régénérer
                </Button>
              </div>
              <p className="text-white/30 text-[11px]">Régénérer invalide immédiatement l'ancienne clé : l'appareil déjà lié devra se réactiver avec la nouvelle.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CREATE PROMO */}
      <ReleaseDialog product={releaseBot} onClose={() => setReleaseBot(null)} onDone={fetchAll} />

      <Dialog open={showAddPromo} onOpenChange={setShowAddPromo}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Tag className="w-4 h-4 text-blue-400" />Code promo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-white/60 text-xs">Code</Label><Input value={promoForm.code} onChange={e => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase().replace(/\s/g, '') })} className="bg-black/30 border-white/10 rounded-lg mt-1 font-mono uppercase" /></div>
            <div><Label className="text-white/60 text-xs">Visibilité</Label>
              <Select value={promoForm.visibility} onValueChange={v => setPromoForm({ ...promoForm, visibility: v })}>
                <SelectTrigger className="bg-black/30 border-white/10 rounded-lg mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12121a] border-white/10"><SelectItem value="public">Public</SelectItem><SelectItem value="private">Privé</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-white/60 text-xs">Type</Label>
              <Select value={promoForm.discount_type} onValueChange={v => setPromoForm({ ...promoForm, discount_type: v })}>
                <SelectTrigger className="bg-black/30 border-white/10 rounded-lg mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12121a] border-white/10"><SelectItem value="percentage">%</SelectItem><SelectItem value="fixed">€</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-white/60 text-xs">Valeur</Label><Input type="number" min="1" value={promoForm.discount_value} onChange={e => setPromoForm({ ...promoForm, discount_value: parseFloat(e.target.value) || 0 })} className="bg-black/30 border-white/10 rounded-lg mt-1" /></div>
            <div><Label className="text-white/60 text-xs">S'applique à</Label>
              <Select value={promoForm.applies_to} onValueChange={v => setPromoForm({ ...promoForm, applies_to: v })}>
                <SelectTrigger className="bg-black/30 border-white/10 rounded-lg mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12121a] border-white/10">
                  <SelectItem value="all">Toute la boutique</SelectItem>
                  {shopBots.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-white/60 text-xs">Max utilisations au total (0=illimité)</Label><Input type="number" min="0" value={promoForm.max_uses} onChange={e => setPromoForm({ ...promoForm, max_uses: parseInt(e.target.value) || 0 })} className="bg-black/30 border-white/10 rounded-lg mt-1" /></div>
            <div>
              <Label className="text-white/60 text-xs">Utilisations max par personne (0=illimité)</Label>
              <Input data-testid="promo-max-per-user" type="number" min="0" value={promoForm.max_uses_per_user} onChange={e => setPromoForm({ ...promoForm, max_uses_per_user: Math.max(0, parseInt(e.target.value) || 0) })} className="bg-black/30 border-white/10 rounded-lg mt-1" />
              <p className="text-[11px] text-white/30 mt-1">Combien de fois un même client peut utiliser ce code (1 = une seule fois).</p>
            </div>
            <Button onClick={createPromo} className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">Créer</Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* ADD ANNOUNCEMENT */}
      <Dialog open={showAddAnnouncement} onOpenChange={setShowAddAnnouncement}>
        <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="w-4 h-4 text-blue-400" />Publier une annonce</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-white/60 text-xs">Titre</Label><Input value={annForm.title} onChange={e => setAnnForm({ ...annForm, title: e.target.value })} className="bg-black/30 border-white/10 rounded-lg mt-1" /></div>
            <div><Label className="text-white/60 text-xs">Contenu</Label><textarea value={annForm.content} onChange={e => setAnnForm({ ...annForm, content: e.target.value })} rows={4} className="w-full bg-black/30 border border-white/10 rounded-lg mt-1 p-3 text-sm text-white resize-none focus:outline-none focus:border-blue-500" /></div>
            <div><Label className="text-white/60 text-xs">URL image (optionnel)</Label>
              <label className="flex items-center justify-center w-full h-24 mt-1 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-blue-500/50 transition-colors bg-black/20">
                {annForm.image_url ? (
                  <div className="relative">
                    <img src={annForm.image_url} alt="" className="h-20 rounded object-cover" />
                    <button onClick={(e) => { e.preventDefault(); setAnnForm({ ...annForm, image_url: "" }); }} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">x</button>
                  </div>
                ) : (
                  <div className="text-center"><Upload className="w-5 h-5 text-white/20 mx-auto mb-1" /><p className="text-xs text-white/30">Cliquez pour uploader</p></div>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  const reader = new FileReader();
                  reader.onload = async () => {
                    try {
                      const r = await axios.post(`${API}/upload/image`, { image: reader.result }, { headers: hdr() });
                      setAnnForm({ ...annForm, image_url: r.data.url }); toast.success("Image uploadée");
                    } catch { toast.error("Erreur upload"); }
                  };
                  reader.readAsDataURL(file);
                }} />
              </label>
            </div>
            <Button onClick={createAnnouncement} className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">Publier</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DELETE */}
      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent className="bg-[#12121a] border-white/10">
          <AlertDialogHeader><AlertDialogTitle>Supprimer {deleteItem?.bot_name} ?</AlertDialogTitle><AlertDialogDescription className="text-white/50">Irréversible.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="border-white/10 rounded-lg">Annuler</AlertDialogCancel><AlertDialogAction onClick={removeBot} className="bg-red-600 hover:bg-red-700 rounded-lg">Supprimer</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {builderBot && (
        <PageBuilder
          bot={builderBot}
          onClose={() => setBuilderBot(null)}
          onSaved={(patch) => {
            setShopBots((prev) => prev.map((b) => (b.id === builderBot.id ? { ...b, ...patch } : b)));
            setBuilderBot(null);
          }}
        />
      )}
    </div>
  );
}
